/* eslint react-hooks/exhaustive-deps: 0 */

import { LeftOutlined, SyncOutlined } from "@ant-design/icons";
import { Alert, Card, Col, Row, Spin, Modal, Button, Table, Progress, Pagination } from "antd";
import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { WebsocketKey, ModalButton, ModalType } from "../../../config/enum";
import { setCurrentStep, setCurrentNode, selectCurrentAppState } from "../../../redux/features/app/appSlice";
import { useProcessTagDataQuery } from "../../../redux/services/tagSlice";
import { useAddTagsToReferenceStockMutation, useDeleteTagsFromReferenceStockMutation } from "../../../redux/services/referenceStockSlice";
import { useExecuteProcessMutation, useStartProcessMutation, useStopActiveProcessMutation, useGetAllActiveProcessQuery, useResumeProcessMutation } from "../../../redux/services/processSlice";
import { useGetDeviceStatusQuery, useGetAllDeviceOfUserQuery } from "../../../redux/services/deviceSite";
import { socket } from "../../../redux/services/webSocket";
import { authStateSelector } from "../../../redux/features/auth/authSlice";
import { message } from "antd";
import { ActivityLogsTable } from "./activitylogsTable";
import { DeviceDetail, readStateIdFromNavRow } from "./deviceDetail";
import { UIConfirmModal } from "../../common/confirmModal";
import UITooltip from "../../common/tooltip";
import "./style.css";
import { Page } from "../../../routes/config";

/** Merge sync payloads without clearing Total Scan Cycles when API/WS omits counts or sends 0 after pause. */
const mergeSyncedDataPreserveCycles = (prev: any, patch: any) => {
  const p = prev && typeof prev === "object" ? prev : {};
  const patchObj = patch && typeof patch === "object" ? patch : {};
  const merged = { ...p, ...patchObj };
  const prevC = p.totalCycles;
  if (typeof prevC === "number" && prevC > 0 && patchObj.totalCycles === 0) {
    merged.totalCycles = prevC;
  }
  return merged;
};

/** Compare MACs regardless of `:` vs `-` separators (API vs inventory vs Keonn). */
const normalizeMacKey = (mac: any) => String(mac || "").replace(/[:-]/g, "").toUpperCase();

export const ActivityLogs = () => {
  const { state }: any = useLocation();

  const [syncedData, setSyncedData] = useState<any>();
  const [showAlert, setShowAlert] = useState(false);
  const [notifyAlert, setNotifyAlert] = useState<any>();
  const hasShownConnectionErrorRef = useRef<boolean>(false);

  // Edit Reference Stock modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [activePanel, setActivePanel] = useState<'scanned' | 'current'>('scanned');
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [currentStock, setCurrentStock] = useState<any[]>([]);
  const [scannedTags, setScannedTags] = useState<any[]>([]);
  const [loadingModalData, setLoadingModalData] = useState(false);
  const [isTagActionLoading, setIsTagActionLoading] = useState(false);
  const currentStockRef = useRef<any[]>([]);
  const scannedTagsRef = useRef<any[]>([]);
  const loadingModalDataRef = useRef<boolean>(false);
  const isTagActionLoadingRef = useRef<boolean>(false);
  // Pagination state for both tables
  const [currentStockPagination, setCurrentStockPagination] = useState({ current: 1, pageSize: 10 });
  const [scannedTagsPagination, setScannedTagsPagination] = useState({ current: 1, pageSize: 10 });
  const ignoreWebSocketUpdatesRef = useRef<boolean>(false);
  const lastManualUpdateTimeRef = useRef<number>(0);
  // Throttling refs for WebSocket updates - limit UI updates to once every 150ms
  const lastWebSocketUpdateTimeRef = useRef<number>(0);
  const pendingWebSocketDataRef = useRef<any>(null);
  const throttlingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const THROTTLE_INTERVAL_MS = 150; // Update UI at most once every 150ms
  
  // Cache for mapped tags - stores tags with product descriptions for 2-3 minutes
  // Key: EPC, Value: { tag data, timestamp }
  const mappedTagsCacheRef = useRef<Map<string, { tag: any; timestamp: number }>>(new Map());
  const TAG_CACHE_DURATION_MS = 2.5 * 60 * 1000; // 2.5 minutes
  const tagCacheCleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoggedFirstEditRefStockPayloadRef = useRef<boolean>(false);
  
  // Track EPCs that were manually moved to currentStock (via Add Tag button)
  // This ensures they stay filtered out from scannedTags even if WebSocket updates come in
  // Key: EPC, Value: timestamp when moved
  const manuallyMovedToCurrentStockRef = useRef<Map<string, number>>(new Map());
  
  // Track EPCs that were manually removed from currentStock (via Delete Tag button)
  // This ensures they stay filtered out from currentStock even if WebSocket updates come in
  // Key: EPC, Value: timestamp when removed
  const manuallyRemovedFromCurrentStockRef = useRef<Map<string, number>>(new Map());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [setReferenceStockProcessId, setSetReferenceStockProcessId] = useState<string | null>(null);
  const [addConfirmVisible, setAddConfirmVisible] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);

  useEffect(() => {
    currentStockRef.current = currentStock;
  }, [currentStock]);

  useEffect(() => {
    scannedTagsRef.current = scannedTags;
  }, [scannedTags]);

  useEffect(() => {
    loadingModalDataRef.current = loadingModalData;
  }, [loadingModalData]);

  useEffect(() => {
    isTagActionLoadingRef.current = isTagActionLoading;
  }, [isTagActionLoading]);
  
  // API mutations
  const [addTagsToReferenceStock] = useAddTagsToReferenceStockMutation();
  const [deleteTagsFromReferenceStock] = useDeleteTagsFromReferenceStockMutation();
  const [executeProcess, executeInfo] = useExecuteProcessMutation();
  const [startProcess, startInfo] = useStartProcessMutation();
  const [stopProcess] = useStopActiveProcessMutation();
  const [resumeProcess] = useResumeProcessMutation();
  
  // Get user info for API calls
  const { user, tenantId } = useSelector(authStateSelector);
  const dispatch = useDispatch();
  
  // Get current app state to check if we're at WAIT node
  const appState = useSelector(selectCurrentAppState);
  const currentNode = appState?.currentNode || "";
  
  // Query to check active processes (used to verify if workflow is actually running)
  const { data: activeProcessesData, refetch: refetchActiveProcesses } = useGetAllActiveProcessQuery(
    {
      queryParams: { page: 1, limit: 100, type: "ActiveProcess", tenantId },
      roleId: user?.roleId
    },
    {
      skip: !user?.roleId || !tenantId // Skip if missing required data
    }
  );
  
  // State for Edit Reference Stock workflow execution
  const [isStartingERS, setIsStartingERS] = useState(false);
  const [ersWorkflowRunning, setErsWorkflowRunning] = useState(false);
  // Progress bar state for Edit Reference Stock modal opening
  const [ersProgress, setErsProgress] = useState(0);
  const [ersProgressStep, setErsProgressStep] = useState('');
  const [ersProgressModalVisible, setErsProgressModalVisible] = useState(false);
  
  // State to track if Check Reference Stock (CRS) is running
  const [crsWorkflowRunning, setCrsWorkflowRunning] = useState(false);
  
  // Callback to track CRS workflow state from DeviceDetail component
  const handleCrsProcessStateChange = (isRunning: boolean) => {
    setCrsWorkflowRunning(isRunning);
  };
  
  // WebSocket listener for Edit Reference Stock data
  const editRefStockListenerRef = useRef<any>(null);

  const data: any = state ? state : undefined;
  // This query will automatically run when data is available
  // RTK Query handles undefined parameters gracefully
  const tagsProcessData = useProcessTagDataQuery({
    processId: data?.data?.processId,
    deviceId: data?.data?.deviceId,
    zoneId: data?.data?.zoneId,
    siteId: data?.data?.siteId,
  }, {
    skip: !data?.data?.processId || !data?.data?.deviceId // Skip if required data is missing
  });

  // Get user's devices to filter device status (same approach as Device Status page)
  const deviceDataQuery = useGetAllDeviceOfUserQuery({
    userId: user?.userId,
    roleName: user?.roleName,
    roleId: user?.roleId
  }, {
    refetchOnMountOrArgChange: true,
    skip: !user?.userId
  });

  // Use the same API as Device Status page - get all device statuses, then filter by MAC
  const getDeviceStatus = useGetDeviceStatusQuery({
    userId: user?.userId,
    roleName: user?.roleName,
    roleId: user?.roleId
  }, {
    refetchOnMountOrArgChange: true, 
    pollingInterval: 5000,
    skip: false // Never skip - always try to fetch device status
  });

  // Get device MAC address from device data query if not available in data.data
  const deviceMacAddress = useMemo(() => {
    // First try to get from data.data
    let mac = data?.data?.macAddress || data?.data?.deviceMac;
    
    // If not found, try to get from deviceDataQuery by matching deviceId
    if (!mac && data?.data?.deviceId && deviceDataQuery?.data?.result?.deviceData) {
      const device = deviceDataQuery.data.result.deviceData.find((d: any) => d.deviceId === data.data.deviceId);
      mac = device?.mac || device?.macAddress;
    }
    
    // If still not found, try to extract MAC address from device name (format: "DeviceName - [MAC]")
    if (!mac && data?.data?.deviceName) {
      const macMatch = data.data.deviceName.match(/\[([0-9a-fA-F]{2}(?::[0-9a-fA-F]{2}){5})\]/i);
      if (macMatch && macMatch[1]) {
        mac = macMatch[1];
        console.log('[Activity Logs] Extracted MAC address from device name', {
          deviceName: data.data.deviceName,
          extractedMac: mac,
          deviceId: data?.data?.deviceId
        });
      }
    }
    
    return mac?.toUpperCase()?.trim();
  }, [data?.data?.macAddress, data?.data?.deviceMac, data?.data?.deviceId, data?.data?.deviceName, deviceDataQuery?.data?.result?.deviceData]);

  // Filter API polling data by the specific device's MAC address (same pattern as Device Status page)
  const apiDeviceStatusFiltered = useMemo(() => {
    if (!deviceMacAddress) {
      return [];
    }
    
    const apiStatus = getDeviceStatus?.data?.result || [];
    
    // Filter for the specific device's MAC address
    const targetKey = normalizeMacKey(deviceMacAddress);
    const filtered = apiStatus.filter((deviceStatus: any) => {
      const statusMac = deviceStatus?.deviceMac;
      return statusMac && normalizeMacKey(statusMac) === targetKey;
    });
    
    return filtered;
  }, [getDeviceStatus?.data?.result, deviceMacAddress]);

  // Log API polling status for device status
  useEffect(() => {
    if (getDeviceStatus?.isError) {
      console.error('[Activity Logs Page] Error fetching device status via API', {
        error: getDeviceStatus?.error,
        userId: user?.userId,
        deviceId: data?.data?.deviceId,
        deviceMac: deviceMacAddress,
        timestamp: new Date().toISOString()
      });
    }
    if (getDeviceStatus?.isSuccess) {
      console.log('[Activity Logs Page] Device status fetched successfully via API', {
        totalStatusCount: getDeviceStatus?.data?.result?.length || 0,
        filteredStatusCount: apiDeviceStatusFiltered.length,
        userId: user?.userId,
        deviceId: data?.data?.deviceId,
        deviceMac: deviceMacAddress,
        deviceMacSource: data?.data?.macAddress || data?.data?.deviceMac ? 'data.data' : (deviceDataQuery?.data?.result?.deviceData ? 'deviceDataQuery' : 'not found'),
        deviceStatus: apiDeviceStatusFiltered[0] ? {
          deviceMac: apiDeviceStatusFiltered[0]?.deviceMac,
          status: apiDeviceStatusFiltered[0]?.status
        } : null,
        timestamp: new Date().toISOString()
      });
    }
  }, [getDeviceStatus?.isError, getDeviceStatus?.isSuccess, getDeviceStatus?.data?.result, apiDeviceStatusFiltered, deviceMacAddress, user?.userId]);

  // State to store device status from WebSocket (following Device Status page pattern)
  const [wsDeviceStatus, setWsDeviceStatus] = useState<any[] | null>(null);

  // WebSocket listener for device status updates (filtered by MAC address - same pattern as Device Status page)
  useEffect(() => {
    if (!user?.userId || !deviceMacAddress) {
      console.log('[Activity Logs Page] WebSocket listener skipped - missing userId or device MAC', {
        userId: user?.userId,
        deviceId: data?.data?.deviceId,
        macAddress: data?.data?.macAddress,
        deviceMac: data?.data?.deviceMac,
        deviceMacAddress: deviceMacAddress,
        deviceDataQueryLoaded: !!deviceDataQuery?.data?.result?.deviceData,
        timestamp: new Date().toISOString()
      });
      return;
    }
    const deviceMac = deviceMacAddress;
    const userSpecificEventName = `${WebsocketKey.DEVICE_STATUS_UPDATE}-${user.userId}`;
    const generalEventName = WebsocketKey.DEVICE_STATUS_UPDATE;

    console.log('[Activity Logs Page] Registering WebSocket listeners for device status', {
      userId: user?.userId,
      deviceMac,
      userSpecificEventName,
      generalEventName,
      timestamp: new Date().toISOString()
    });

    const handleDeviceStatusUpdate = (wsData: any) => {
      console.log('[Activity Logs Page] WebSocket device status update received', {
        userId: user?.userId,
        deviceMac,
        eventName: userSpecificEventName,
        wsDataKeys: Object.keys(wsData || {}),
        hasResult: !!wsData?.result,
        hasDataResult: !!wsData?.data?.result,
        fullWsData: wsData, // Log full structure to see what we're actually receiving
        timestamp: new Date().toISOString()
      });

      let result: any[] = [];
      
      // Try multiple data structure patterns
      // Pattern 1: wsData.result (array)
      if (wsData?.result && Array.isArray(wsData.result)) {
        result = wsData.result;
      } 
      // Pattern 2: wsData.data.result (nested result)
      else if (wsData?.data?.result && Array.isArray(wsData.data.result)) {
        result = wsData.data.result;
      } 
      // Pattern 3: wsData.data (direct array - THIS IS THE ACTUAL STRUCTURE!)
      else if (wsData?.data && Array.isArray(wsData.data)) {
        result = wsData.data;
      }
      // Pattern 4: wsData itself is an array
      else if (Array.isArray(wsData)) {
        result = wsData;
      }
      // Pattern 5: Check if wsData.data has a nested structure
      else if (wsData?.data && typeof wsData.data === 'object' && !Array.isArray(wsData.data)) {
        // Try to find array in nested structure
        const dataKeys = Object.keys(wsData.data);
        console.log('[Activity Logs Page] Checking nested data structure', {
          dataKeys,
          wsDataData: wsData.data,
          timestamp: new Date().toISOString()
        });
        
        // Check common nested patterns
        if (Array.isArray(wsData.data.deviceStatus)) {
          result = wsData.data.deviceStatus;
        } else if (Array.isArray(wsData.data.status)) {
          result = wsData.data.status;
        } else if (Array.isArray(wsData.data.devices)) {
          result = wsData.data.devices;
        } else if (Array.isArray(wsData.data.result)) {
          result = wsData.data.result;
        }
        
        if (result.length > 0) {
          console.log('[Activity Logs Page] Device status from nested wsData.data structure', {
            count: result.length,
            deviceMacs: result.map((d: any) => d?.deviceMac).filter(Boolean)
          });
        }
      }
      
      if (Array.isArray(result) && result.length > 0) {
        console.log('[Activity Logs Page] Processing WebSocket device status array', {
          totalCount: result.length,
          deviceMacs: result.map((d: any) => d?.deviceMac),
          targetDeviceMac: deviceMac,
          timestamp: new Date().toISOString()
        });

        // Filter for the specific device's MAC address (normalize both for comparison)
        const normalizedTargetMac = normalizeMacKey(deviceMac);
        const deviceSpecificStatus = result.filter((d: any) => {
          const statusMac = d?.deviceMac;
          return statusMac && normalizeMacKey(statusMac) === normalizedTargetMac;
        });
        
        console.log('[Activity Logs Page] Filtered device status', {
          beforeFilter: result.length,
          afterFilter: deviceSpecificStatus.length,
          matched: deviceSpecificStatus.length > 0,
          targetMac: normalizedTargetMac,
          foundMacs: result.map((d: any) => d?.deviceMac?.toUpperCase()?.trim()),
          deviceStatus: deviceSpecificStatus[0] ? {
            deviceMac: deviceSpecificStatus[0]?.deviceMac,
            status: deviceSpecificStatus[0]?.status
          } : null,
          timestamp: new Date().toISOString()
        });
        
        if (deviceSpecificStatus.length > 0) {
          setWsDeviceStatus(deviceSpecificStatus);
          
          // Update cycle counts from device status if processObject contains cycle data
          // Device status WebSocket events contain processObject with totalCycles, totalSuccess, totalFailed
          deviceSpecificStatus.forEach((deviceStatus: any) => {
            if (deviceStatus?.processObject || deviceStatus?.processId === data?.data?.processId) {
              updateCycleCountsFromWebSocket(deviceStatus);
            }
          });
        } else {
          // Broadcast may only include other readers (e.g. Keonn traffic for another MAC). Do not keep stale "online" for this device.
          setWsDeviceStatus(null);
        }
      } else {
        console.warn('[Activity Logs Page] WebSocket data structure unexpected - logging full structure', {
          wsData,
          wsDataType: typeof wsData,
          isArray: Array.isArray(wsData),
          stringified: JSON.stringify(wsData, null, 2),
          timestamp: new Date().toISOString()
        });
      }
    };

    socket.on(userSpecificEventName, handleDeviceStatusUpdate);
    socket.on(generalEventName, handleDeviceStatusUpdate);

    console.log('[Activity Logs Page] WebSocket listeners registered for device status', {
      userSpecificEventName,
      generalEventName,
      timestamp: new Date().toISOString()
    });

    return () => {
      console.log('[Activity Logs Page] Cleaning up WebSocket listeners for device status', {
        userSpecificEventName,
        generalEventName,
        timestamp: new Date().toISOString()
      });
      socket.off(userSpecificEventName, handleDeviceStatusUpdate);
      socket.off(generalEventName, handleDeviceStatusUpdate);
    };
  }, [user?.userId, deviceMacAddress]);

  // Merge WebSocket with polling: prefer API when present (same source as CRS / device manager).
  // WebSocket payloads are often partial; another reader's heartbeat must not imply this device is online.
  const mergedDeviceStatus = useMemo(() => {
    const api = apiDeviceStatusFiltered?.[0];
    const ws = wsDeviceStatus?.[0];
    if (api) return [api];
    if (ws) return [ws];
    return [];
  }, [wsDeviceStatus, apiDeviceStatusFiltered]);
  
  // Log merged device status
  useEffect(() => {
    console.log('[Activity Logs Page] Merged device status', {
      wsDeviceStatusCount: wsDeviceStatus?.length || 0,
      apiDeviceStatusCount: apiDeviceStatusFiltered?.length || 0,
      mergedCount: mergedDeviceStatus.length,
      dataSource: apiDeviceStatusFiltered?.length ? 'API (preferred)' : (wsDeviceStatus?.length ? 'WebSocket' : 'None'),
      deviceStatus: mergedDeviceStatus[0] ? {
        deviceMac: mergedDeviceStatus[0]?.deviceMac,
        status: mergedDeviceStatus[0]?.status
      } : null,
      targetDeviceMac: data?.data?.macAddress || data?.data?.deviceMac,
      apiQueryStatus: {
        isLoading: getDeviceStatus?.isLoading,
        isError: getDeviceStatus?.isError,
        isSuccess: getDeviceStatus?.isSuccess,
        isSkipped: getDeviceStatus?.isUninitialized,
        error: getDeviceStatus?.error
      },
      wsDeviceStatusData: wsDeviceStatus,
      apiDeviceStatusData: getDeviceStatus?.data?.result,
      filteredApiDeviceStatusData: apiDeviceStatusFiltered,
      timestamp: new Date().toISOString()
    });
  }, [wsDeviceStatus, apiDeviceStatusFiltered, mergedDeviceStatus, getDeviceStatus?.isLoading, getDeviceStatus?.isError, getDeviceStatus?.isSuccess, getDeviceStatus?.isUninitialized, getDeviceStatus?.error, getDeviceStatus?.data?.result, data?.data?.macAddress, data?.data?.deviceMac]);

  const [loadingBar, setLoadingBar] = useState(false);
  const navigate = useNavigate();

  // Set up LastSync WebSocket listener only when data is available
  useEffect(() => {
    if (!data?.data?.deviceId || !data?.data?.processId) {
      return;
    }

    // New pattern: {type}-{siteId}-{zoneId}-{deviceId}-{processId}
    const siteId = data?.data?.siteId || '';
    const zoneId = data?.data?.zoneId || '';
    const deviceId = data?.data?.deviceId;
    const processId = data?.data?.processId;
    
    const lastSyncEventName = `${WebsocketKey.LastSync}-${siteId}-${zoneId}-${deviceId}-${processId}`;
    const handleLastSync = (lastSyncdata: any) => {
      if (
        data?.data?.processId === lastSyncdata?.data?.processId &&
        data?.data?.deviceId === lastSyncdata?.data?.deviceId
      ) {
        setSyncedData((prev: any) => mergeSyncedDataPreserveCycles(prev, lastSyncdata?.data));
      }
    };

    socket.on(lastSyncEventName, handleLastSync);
    
    return () => {
      socket.off(lastSyncEventName, handleLastSync);
    };
  }, [data?.data?.deviceId, data?.data?.processId, data?.data?.siteId, data?.data?.zoneId]);

  useEffect(() => {
    if (tagsProcessData?.data) {
      setSyncedData({ ...tagsProcessData?.data?.result });
    }
  }, [tagsProcessData?.data, tagsProcessData?.isSuccess]);

  // Track scan cycle processing to avoid per-tag updates
  const lastProcessedCycleRef = useRef<number | null>(null);
  const lastProcessedCycleProcessIdRef = useRef<string | null>(null);
  const lastProcessedCycleAtRef = useRef<number>(0);
  const pendingTagActionsRef = useRef<Map<string, { action: 'add' | 'delete'; timestamp: number }>>(new Map());
  const TAG_ACTION_TTL_MS = 30000;

  const isBatchEditRefStockPayload = (payload: any) => {
    if (!payload || typeof payload !== 'object') {
      return false;
    }
    const data = payload?.data?.data || payload?.data || payload;
    const hasArrays =
      Array.isArray(data?.currentStock) ||
      Array.isArray(data?.existingData) ||
      Array.isArray(data?.scannedTags) ||
      Array.isArray(data?.missingTags) ||
      Array.isArray(data?.extraTags);
    return hasArrays;
  };

  const getCycleKeyFromWebSocket = (wsData: any) => {
    const processObject = wsData?.processObject || wsData?.data?.processObject || wsData?.data?.data?.processObject;
    const totalCycles = processObject?.totalCycles ??
      wsData?.totalCycles ??
      wsData?.data?.totalCycles ??
      wsData?.data?.data?.totalCycles;
    return typeof totalCycles === 'number' ? totalCycles : undefined;
  };

  const shouldProcessScanBatch = (wsData: any) => {
    const payloadProcessId = wsData?.data?.processId || wsData?.processId;
    if (payloadProcessId && payloadProcessId !== lastProcessedCycleProcessIdRef.current) {
      lastProcessedCycleProcessIdRef.current = payloadProcessId;
      lastProcessedCycleRef.current = null;
    }

    const cycleKey = getCycleKeyFromWebSocket(wsData);
    if (cycleKey !== undefined) {
      if (lastProcessedCycleRef.current === cycleKey) {
        return false;
      }
      lastProcessedCycleRef.current = cycleKey;
      return true;
    }

    // Fallback: allow at most one batch per short window when cycle info is missing
    const now = Date.now();
    if (now - lastProcessedCycleAtRef.current < 800) {
      return false;
    }
    lastProcessedCycleAtRef.current = now;
    return true;
  };

  const cleanupPendingTagActions = () => {
    const now = Date.now();
    pendingTagActionsRef.current.forEach((value, epc) => {
      if (now - value.timestamp > TAG_ACTION_TTL_MS) {
        pendingTagActionsRef.current.delete(epc);
      }
    });
  };

  const applyPendingActionsToCurrentStock = (processedCurrentStock: any[]) => {
    cleanupPendingTagActions();
    const currentByEpc = new Map(
      processedCurrentStock.map(item => [item?.epc.trim(), item])
    );
    const optimisticByEpc = new Map(
      currentStockRef.current.map(item => [item?.epc.trim(), item])
    );

    pendingTagActionsRef.current.forEach((action, epc) => {
      if (!epc) return;
      if (action.action === 'delete') {
        currentByEpc.delete(epc);
      } else if (action.action === 'add') {
        if (!currentByEpc.has(epc) && optimisticByEpc.has(epc)) {
          currentByEpc.set(epc, optimisticByEpc.get(epc));
        }
      }
    });

    return Array.from(currentByEpc.values());
  };

  // Helper function to update cycle counts in syncedData from WebSocket events
  const updateCycleCountsFromWebSocket = (wsData: any) => {
    // Check multiple possible locations for cycle counts
    const processObject = wsData?.processObject || wsData?.data?.processObject || wsData?.data?.data?.processObject;
    const totalCycles = processObject?.totalCycles ?? wsData?.totalCycles ?? wsData?.data?.totalCycles ?? wsData?.data?.data?.totalCycles;
    const totalSuccess = processObject?.totalSuccess ?? wsData?.totalSuccess ?? wsData?.data?.totalSuccess ?? wsData?.data?.data?.totalSuccess;
    const totalFailed = processObject?.totalFailed ?? wsData?.totalFailed ?? wsData?.data?.totalFailed ?? wsData?.data?.data?.totalFailed;

    // Only update if we have at least one cycle count value
    if (totalCycles !== undefined || totalSuccess !== undefined || totalFailed !== undefined) {
      setSyncedData((prevSyncedData: any) => {
        const keepPrevCycles =
          totalCycles !== undefined &&
          typeof prevSyncedData?.totalCycles === "number" &&
          prevSyncedData.totalCycles > 0 &&
          totalCycles === 0;
        const updated = {
          ...prevSyncedData,
          ...(totalCycles !== undefined && !keepPrevCycles && { totalCycles }),
          ...(totalSuccess !== undefined && { totalSuccess }),
          ...(totalFailed !== undefined && { totalFailed })
        };
        
        // Only log if values actually changed
        if (prevSyncedData?.totalCycles !== totalCycles || 
            prevSyncedData?.totalSuccess !== totalSuccess || 
            prevSyncedData?.totalFailed !== totalFailed) {
          console.log('[Activity Logs] Updated cycle counts from WebSocket:', {
            previous: {
              totalCycles: prevSyncedData?.totalCycles,
              totalSuccess: prevSyncedData?.totalSuccess,
              totalFailed: prevSyncedData?.totalFailed
            },
            updated: {
              totalCycles,
              totalSuccess,
              totalFailed
            },
            source: processObject ? 'processObject' : 'direct',
            timestamp: new Date().toISOString()
          });
        }
        
        return updated;
      });
    }
  };

  const handleClose = () => setShowAlert(false);

  // Auto-dismiss error alert if device comes back online
  useEffect(() => {
    if (showAlert) {
      const deviceStatus = mergedDeviceStatus?.[0]?.status;
      const isDeviceOnline = deviceStatus === "connected" || deviceStatus === "running";
      
      if (isDeviceOnline) {
        console.log('[ActivityLogs] Device came back online - dismissing error alert', {
          deviceStatus,
          previousError: notifyAlert?.error?.message || notifyAlert?.message
        });
        setShowAlert(false);
        setNotifyAlert(null);
      }
    }
  }, [showAlert, mergedDeviceStatus, notifyAlert]);

  // Helper function to move to WAIT node and show connection error
  const moveToWaitNodeAndShowError = () => {
    // Only show error once
    if (hasShownConnectionErrorRef.current) {
      console.log('[ActivityLogs] Connection error already shown - skipping duplicate error');
      return;
    }

    // Get steps from data to find WAIT node
    let beforeStep = data?.data?.steps
      ? data?.data?.steps
      : data?.data?.processData?.[0]?.steps;
    let steps = beforeStep
      ? typeof beforeStep[0] === "string"
        ? JSON.parse(beforeStep)
        : beforeStep
      : [];

    // Find WAIT node index
    const normalize = (str: string) => str?.toLowerCase().replace(/[\s_-]/g, '') || '';
    const waitStepIndex = steps.findIndex((step: any) => {
      const stepName = step?.name || step;
      return normalize(stepName) === 'wait';
    });

    if (waitStepIndex !== -1) {
      // Move to WAIT node
      dispatch(setCurrentStep({
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        step: waitStepIndex
      }));
      dispatch(setCurrentNode('Wait'));
      console.log('[ActivityLogs] Moved to WAIT node due to device connection error');
    }

    // Show error only once
    hasShownConnectionErrorRef.current = true;
    const errorMessage = "Error while connected to the device";
    setShowAlert(true);
    setNotifyAlert({
      error: { message: errorMessage },
      message: errorMessage
    });
  };

  // Track previous device status to detect reconnection
  const previousDeviceStatusRef = useRef<string | undefined>(undefined);
  const isAutoResumingRef = useRef<boolean>(false);

  // Device connection is checked:
  // - Every 5 seconds via API polling (getDeviceStatus query with pollingInterval: 5000)
  // - Real-time via WebSocket updates (device-status-update events)
  useEffect(() => {
    // Log device connection check (for debugging - shows check frequency)
    const checkTimestamp = new Date().toISOString();
    console.log('[ActivityLogs] Device connection check', {
      timestamp: checkTimestamp,
      checkFrequency: 'Every 5 seconds (API polling) + Real-time (WebSocket)',
      mergedDeviceStatusLength: mergedDeviceStatus?.length || 0,
      currentNode,
      apiPollingInterval: '5000ms',
      websocketUpdates: 'Real-time',
      note: 'Device status is checked every 5 seconds via API, plus instant WebSocket updates when device status changes'
    });
    
    // Get device status from mergedDeviceStatus first, fallback to direct API check
    let deviceStatus = mergedDeviceStatus?.[0]?.status;
    
    // Fallback: Check directly in API response if mergedDeviceStatus is empty
    if (!deviceStatus && deviceMacAddress && getDeviceStatus?.data?.result) {
      const apiStatus = getDeviceStatus.data.result;
      const matchingDevice = apiStatus.find((d: any) => {
        const statusMac = d?.deviceMac?.toUpperCase()?.trim();
        return statusMac === deviceMacAddress;
      });
      if (matchingDevice) {
        deviceStatus = matchingDevice?.status;
      }
    }
    
    // Also try to find by deviceId as fallback
    if (!deviceStatus && data?.data?.deviceId && getDeviceStatus?.data?.result) {
      const apiStatus = getDeviceStatus.data.result;
      const matchingDevice = apiStatus.find((d: any) => {
        return d?.deviceId === data.data.deviceId || 
               (d?.processObject?.deviceId === data.data.deviceId);
      });
      if (matchingDevice) {
        deviceStatus = matchingDevice?.status;
      }
    }
    
    const normalizedStatus = deviceStatus?.toLowerCase()?.trim();
    const isDeviceOnline = normalizedStatus === "connected" || 
                          normalizedStatus === "running" ||
                          normalizedStatus === "active-running" ||
                          normalizedStatus === "active" ||
                          deviceStatus === "connected" || 
                          deviceStatus === "running";
    
    const previousStatus = previousDeviceStatusRef.current;
    const normalizedPreviousStatus = previousStatus?.toLowerCase()?.trim();
    const wasDeviceOffline = normalizedPreviousStatus === "offline" || 
                            normalizedPreviousStatus === "disconnected" ||
                            previousStatus === "offline" || 
                            previousStatus === "disconnected";
    
    // Also check if previous status was undefined/null and now it's online (device just appeared)
    const deviceJustAppeared = (previousStatus === undefined || previousStatus === null) && isDeviceOnline;
    
    // Check if device just reconnected (was offline, now online) OR device just appeared (was undefined, now online)
    const deviceJustReconnected = (wasDeviceOffline && isDeviceOnline) || deviceJustAppeared;
    
    // Check if we're at WAIT node
    const isAtWaitNode = currentNode === "Wait" || currentNode === "WAIT" || currentNode?.toLowerCase() === "wait";
    
    // Update previous status
    previousDeviceStatusRef.current = deviceStatus;
    
    const normalizeProcessState = (value: any) => String(value || "").toLowerCase().trim();
    const isPausedState = (value: any) => {
      const normalized = normalizeProcessState(value);
      return normalized === "pause" || normalized === "pause-processing" || normalized === "pause_processing";
    };

    const rowStateValue = data?.data?.isReadyForProcessing ?? data?.data?.processStates;
    const isPausedInCurrentRow = isPausedState(rowStateValue);

    const activeList = activeProcessesData?.result || [];
    const matchedActiveRow = activeList.find(
      (p: any) =>
        p.processId === data?.data?.processId &&
        p.deviceId === data?.data?.deviceId &&
        (data?.data?.siteId == null ||
          data?.data?.siteId === "" ||
          p.siteId == null ||
          String(p.siteId) === String(data?.data?.siteId)) &&
        (data?.data?.zoneId == null ||
          data?.data?.zoneId === "" ||
          p.zoneId == null ||
          String(p.zoneId) === String(data?.data?.zoneId))
    );
    const activeRowStateValue = matchedActiveRow?.isReadyForProcessing ?? matchedActiveRow?.processStates;
    const isPausedInActiveRow = isPausedState(activeRowStateValue);
    const shouldBlockAutoResume = isPausedInCurrentRow || isPausedInActiveRow;

    // Auto-resume if device reconnected and we're at WAIT node
    if (deviceJustReconnected && isAtWaitNode && !isAutoResumingRef.current && data?.data?.processId && data?.data?.deviceId) {
      if (shouldBlockAutoResume) {
        console.log('[ActivityLogs] Skipping auto-resume because process is in paused state', {
          processId: data?.data?.processId,
          deviceId: data?.data?.deviceId,
          rowStateValue,
          activeRowStateValue,
          timestamp: new Date().toISOString()
        });
        return;
      }

      isAutoResumingRef.current = true;
      
      console.log('[ActivityLogs] Device reconnected - automatically resuming process from WAIT node', {
        previousStatus,
        currentStatus: deviceStatus,
        currentNode,
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        timestamp: new Date().toISOString()
      });
      
      const row = data?.data;
      const stateIdForResume =
        row?.stateId ??
        readStateIdFromNavRow(row) ??
        readStateIdFromNavRow(
          activeList.find(
            (p: any) =>
              p.processId === row?.processId &&
              p.deviceId === row?.deviceId &&
              (row?.siteId == null ||
                row?.siteId === "" ||
                p.siteId == null ||
                String(p.siteId) === String(row?.siteId)) &&
              (row?.zoneId == null ||
                row?.zoneId === "" ||
                p.zoneId == null ||
                String(p.zoneId) === String(row?.zoneId))
          )
        );

      if (!stateIdForResume) {
        console.warn('[ActivityLogs] Skipping auto-resume because stateId is missing', {
          processId: data?.data?.processId,
          deviceId: data?.data?.deviceId,
          timestamp: new Date().toISOString(),
        });
        isAutoResumingRef.current = false;
        return;
      }

      // Call resume process API
      const resumePayload = {
        userId: user?.userId,
        stateId: stateIdForResume,
        processId: data?.data?.processId,
        deviceId: data?.data?.deviceId,
        siteId: data?.data?.siteId,
        zoneId: data?.data?.zoneId,
        tenantId: data?.data?.tenantId || tenantId
      };
      
      resumeProcess(resumePayload)
        .unwrap()
        .then((result) => {
          console.log('[ActivityLogs] Process auto-resumed successfully after device reconnection', {
            result,
            processId: data?.data?.processId,
            deviceId: data?.data?.deviceId,
            timestamp: new Date().toISOString()
          });
          
          // Reset flags
          hasShownConnectionErrorRef.current = false;
          setShowAlert(false);
          setNotifyAlert(null);
          
          // Show success message
          message.success({
            content: "Device reconnected - Process resumed automatically",
            key: "autoResumeSuccess",
            duration: 3
          });
          
          // Reset auto-resuming flag after a delay
          setTimeout(() => {
            isAutoResumingRef.current = false;
          }, 2000);
        })
        .catch((error) => {
          console.error('[ActivityLogs] Failed to auto-resume process after device reconnection', {
            error,
            processId: data?.data?.processId,
            deviceId: data?.data?.deviceId,
            timestamp: new Date().toISOString()
          });
          
          // Reset auto-resuming flag on error
          isAutoResumingRef.current = false;
          
          console.log('[ActivityLogs] Auto-resume failed - process will remain at WAIT node. Backend may retry automatically.');
        });
    } else if (isDeviceOnline && hasShownConnectionErrorRef.current) {
      // Device is online and we had shown connection error - just reset the flag
      console.log('[ActivityLogs] Device came back online - resetting connection error flag');
      hasShownConnectionErrorRef.current = false;
      setShowAlert(false);
      setNotifyAlert(null);
    }
  }, [mergedDeviceStatus, currentNode, data?.data?.processId, data?.data?.deviceId, data?.data?.siteId, data?.data?.zoneId, data?.data?.tenantId, data?.data?.stateId, data?.data?._id, user?.userId, tenantId, resumeProcess, deviceMacAddress, getDeviceStatus?.data?.result, activeProcessesData?.result]);

  // Set up Notification_error WebSocket listener
  useEffect(() => {
    if (!data?.data?.deviceId || !data?.data?.processId) {
      return;
    }

    // New pattern: {type}-{siteId}-{zoneId}-{deviceId}-{processId}
    const siteId = data?.data?.siteId || '';
    const zoneId = data?.data?.zoneId || '';
    const deviceId = data?.data?.deviceId;
    const processId = data?.data?.processId;
    
    const notificationEventName = `${WebsocketKey.Notification_error}-${siteId}-${zoneId}-${deviceId}-${processId}`;
    const handleNotificationError = (payload: any) => {
      if (
        window.location.pathname === Page.ACTIVITY_LOGS &&
        data?.data?.processId === payload?.processId &&
        data?.data?.deviceId === payload?.deviceId
      ) {
        const deviceStatus = mergedDeviceStatus?.[0]?.status;
        const isDeviceOnline = deviceStatus === "connected" || deviceStatus === "running";
        
        // Only show error if device is actually offline
        if (!isDeviceOnline) {
          console.log('[ActivityLogs] Device connection error detected - moving to WAIT node', {
            deviceStatus,
            errorMessage: payload?.data?.error?.message || payload?.data?.message
          });
          moveToWaitNodeAndShowError();
        } else {
          console.log('[ActivityLogs] Ignoring connection error - device is online', {
            deviceStatus,
            errorMessage: payload?.data?.error?.message || payload?.data?.message,
            note: 'Device status shows online, so this error is likely a false positive'
          });
        }
      }
    };

    socket.on(notificationEventName, handleNotificationError);

    return () => {
      socket.off(notificationEventName, handleNotificationError);
    };
  }, [data?.data?.deviceId, data?.data?.processId, data?.data?.siteId, data?.data?.zoneId, mergedDeviceStatus, dispatch, data?.data?.steps, data?.data?.processData]);

  // Set up ReaderIssues WebSocket listener
  useEffect(() => {
    if (!data?.data?.deviceId || !data?.data?.processId) {
      return;
    }

    // New pattern: {type}-{siteId}-{zoneId}-{deviceId}-{processId}
    const siteId = data?.data?.siteId || '';
    const zoneId = data?.data?.zoneId || '';
    const deviceId = data?.data?.deviceId;
    const processId = data?.data?.processId;
    
    const readerIssuesEventName = `${WebsocketKey.ReaderIssues}-${siteId}-${zoneId}-${deviceId}-${processId}`;
    const handleReaderIssues = (payload: any) => {
      if (
        window.location.pathname === Page.ACTIVITY_LOGS &&
        data?.data?.processId === payload?.processId &&
        data?.data?.deviceId === payload?.deviceId
      ) {
        const deviceStatus = mergedDeviceStatus?.[0]?.status;
        const isDeviceOnline = deviceStatus === "connected" || deviceStatus === "running";
        
        if (!isDeviceOnline) {
          console.log('[ActivityLogs] Device connection error detected (ReaderIssues) - moving to WAIT node', {
            deviceStatus,
            errorMessage: payload?.data?.error?.message || payload?.data?.message
          });
          moveToWaitNodeAndShowError();
        } else {
          console.log('[ActivityLogs] Ignoring reader issues error - device is online', {
            deviceStatus,
            errorMessage: payload?.data?.error?.message || payload?.data?.message,
            note: 'Device status shows online, so this error is likely a false positive'
          });
        }
      }
    };

    socket.on(readerIssuesEventName, handleReaderIssues);

    return () => {
      socket.off(readerIssuesEventName, handleReaderIssues);
    };
  }, [data?.data?.deviceId, data?.data?.processId, data?.data?.siteId, data?.data?.zoneId, mergedDeviceStatus, dispatch, data?.data?.steps, data?.data?.processData]);

  const refresh = () => window.location.reload();

  const ensureSocketConnected = async (): Promise<boolean> => {
    if (socket.connected) {
      return true;
    }

    const authUserId = user?.userId;
    const authTenantId = tenantId || data?.data?.tenantId;
    socket.auth = { userId: authUserId, tenantId: authTenantId };
    socket.connect();

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        cleanup();
        resolve(false);
      }, 3000);

      const handleConnect = () => {
        cleanup();
        resolve(true);
      };

      const handleConnectError = (error: any) => {
        console.error('[Edit Reference Stock] Socket connect_error while waiting', {
          message: error?.message,
          description: error?.description,
          context: error?.context
        });
      };

      const cleanup = () => {
        clearTimeout(timeout);
        socket.off('connect', handleConnect);
        socket.off('connect_error', handleConnectError);
      };

      socket.on('connect', handleConnect);
      socket.on('connect_error', handleConnectError);
    });
  };

  // WebSocket listener for Edit Reference Stock data
  useEffect(() => {
    if (!data?.data?.deviceId || !data?.data?.processId) {
      console.log('[Edit Reference Stock] WebSocket listener: Skipping setup - missing deviceId or processId', {
        deviceId: data?.data?.deviceId,
        processId: data?.data?.processId
      });
      return;
    }

    const handleSocketConnect = () => {
      console.log('[Edit Reference Stock] Socket connected', {
        socketId: socket.id,
        transport: socket.io?.engine?.transport?.name
      });
    };

    const handleSocketConnectError = (error: any) => {
      console.error('[Edit Reference Stock] Socket connection error', {
        message: error?.message,
        description: error?.description,
        context: error?.context
      });
    };

    socket.on('connect', handleSocketConnect);
    socket.on('connect_error', handleSocketConnectError);

    if (!socket.connected) {
      socket.auth = { userId: user?.userId, tenantId };
      console.log('[Edit Reference Stock] Socket not connected - attempting reconnect', {
        hasUserId: Boolean(user?.userId),
        hasTenantId: Boolean(tenantId)
      });
      socket.connect();
    }

    // New pattern: {type}-{siteId}-{zoneId}-{deviceId}-{processId}
    const siteId = data?.data?.siteId || '';
    const zoneId = data?.data?.zoneId || '';
    const deviceId = data?.data?.deviceId;
    const processId = data?.data?.processId; // Check Reference Stock processId (execution processId)
    
    // ISSUE: Backend stores state with Set Reference Stock processId, but might send WebSocket messages with:
    // - Check Reference Stock processId (execution processId): a348e8d8-9177-4f34-8956-123fd8ffaa89
    // - Set Reference Stock processId (storage processId): 8dfe2937-3ca5-49f3-a462-0acc295b15d7
    // We need to listen to both event names to catch messages regardless of which processId backend uses
    
    // Primary event name: edit-reference-stock-data with site-zone-device-process pattern
    const primaryEventName = `${WebsocketKey.EDIT_REFERENCE_STOCK_DATA}-${siteId}-${zoneId}-${deviceId}-${processId}`;
    
    
    const setReferenceStockEventName = setReferenceStockProcessId 
      ? `${WebsocketKey.EDIT_REFERENCE_STOCK_DATA}-${siteId}-${zoneId}-${deviceId}-${setReferenceStockProcessId}`
      : null;
    
    
    const siteZoneDeviceEventName = `${WebsocketKey.EDIT_REFERENCE_STOCK_DATA}-${siteId}-${zoneId}-${deviceId}`;
    
    const baseEventName = WebsocketKey.EDIT_REFERENCE_STOCK_DATA;
    
    // Also listen to check-reference-stock messages as another fallback
    const checkRefStockEventName = `${WebsocketKey.CHECK_REF_STOCK}-${siteId}-${zoneId}-${deviceId}-${processId}`;
    
    console.log('[Edit Reference Stock] WebSocket listener: Setting up WebSocket listeners', {
      timestamp: new Date().toISOString(),
      primaryEventName: primaryEventName,
      setReferenceStockEventName: setReferenceStockEventName,
      siteZoneDeviceEventName: siteZoneDeviceEventName,
      baseEventName: baseEventName,
      fallbackEventName: checkRefStockEventName,
      siteId: siteId,
      zoneId: zoneId,
      deviceId: deviceId,
      executionProcessId: processId,
      storageProcessId: setReferenceStockProcessId,
      note: 'Backend emits to filtered pattern: {type}-{siteId}-{zoneId}-{deviceId}-{processId}. Listening to primary pattern, Set Reference Stock processId pattern, site-zone-device pattern, and base event.'
    });
    
    const applyThrottledUpdate = (wsData: any) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastWebSocketUpdateTimeRef.current;
      
      // Store the latest data (always keep the most recent)
      pendingWebSocketDataRef.current = wsData;
      
      // If enough time has passed, update immediately
      if (timeSinceLastUpdate >= THROTTLE_INTERVAL_MS) {
        lastWebSocketUpdateTimeRef.current = now;
        processWebSocketData(wsData);
        
        // Clear any pending timeout since we updated immediately
        if (throttlingTimeoutRef.current) {
          clearTimeout(throttlingTimeoutRef.current);
          throttlingTimeoutRef.current = null;
        }
      } else {
        // Schedule update after remaining time
        const remainingTime = THROTTLE_INTERVAL_MS - timeSinceLastUpdate;
        
        
        if (throttlingTimeoutRef.current) {
          clearTimeout(throttlingTimeoutRef.current);
        }
        
        throttlingTimeoutRef.current = setTimeout(() => {
          if (pendingWebSocketDataRef.current) {
            lastWebSocketUpdateTimeRef.current = Date.now();
            processWebSocketData(pendingWebSocketDataRef.current);
            pendingWebSocketDataRef.current = null;
          }
          throttlingTimeoutRef.current = null;
        }, remainingTime);
      }
    };
    
    // Process WebSocket data and update UI
    const processWebSocketData = (wsData: any) => {
      if (isTagActionLoadingRef.current) {
        console.log('[Edit Reference Stock] Skipping WebSocket update - tag action in progress');
        return;
      }

      // Also skip WebSocket updates shortly after manual add/delete to prevent overwrites
      const timeSinceLastManualUpdate = Date.now() - lastManualUpdateTimeRef.current;
      if (ignoreWebSocketUpdatesRef.current && timeSinceLastManualUpdate < 5000) {
        console.log('[Edit Reference Stock] Skipping WebSocket update - recent manual operation (add/delete)', {
          timeSinceLastManualUpdate,
          ignoreFlag: ignoreWebSocketUpdatesRef.current
        });
        return;
      }
      
      // Update currentStock and scannedTags from WebSocket data
      if (wsData?.data?.currentStock || wsData?.data?.existingData) {
        const stockData = wsData.data.currentStock || wsData.data.existingData;
        
        // Filter out tags that were manually removed from currentStock (via Delete Tag button)
        // Normalize EPCs for consistent matching
        const filteredStockData = stockData.filter((item: any) => {
          const normalizedEpc = item.epc;
          const isManuallyRemoved = normalizedEpc && (
            manuallyRemovedFromCurrentStockRef.current.has(normalizedEpc) ||
            manuallyRemovedFromCurrentStockRef.current.has(item.epc)
          );
          if (isManuallyRemoved) {
            console.log('[Edit Reference Stock] Filtering out tag from currentStock (manually removed):', {
              originalEpc: item.epc,
              normalizedEpc: normalizedEpc,
              tagId: item.tagId
            });
          }
          return !isManuallyRemoved;
        });
        
        const processedCurrentStock = filteredStockData.map((item: any, index: number) => ({
          ...item,
          id: `current-${item.epc}-${item.tagId || index}`,
          key: `current-${item.epc}-${item.tagId || index}`,
          description: item.productDescription || item.description || '', // Backend automatically enriches with productDescription
          isVerified: item.isVerified !== undefined ? item.isVerified : true
        }));
        
        // When backend confirms tags are in currentStock, we can keep them tracked in manuallyMovedToCurrentStockRef
        
        const mergedCurrentStock = applyPendingActionsToCurrentStock(processedCurrentStock);
        console.log('[Edit Reference Stock] Processing currentStock from edit-reference-stock-data:', {
          count: mergedCurrentStock.length,
          originalCount: stockData.length,
          filteredOutCount: stockData.length - processedCurrentStock.length,
          verifiedCount: wsData?.data?.verifiedCount,
          totalCount: wsData?.data?.totalCount,
          missingCount: wsData?.data?.missingCount,
          scannedCount: wsData?.data?.scannedCount,
          manuallyMovedCount: manuallyMovedToCurrentStockRef.current.size,
          manuallyRemovedCount: manuallyRemovedFromCurrentStockRef.current.size,
          sampleItems: mergedCurrentStock.slice(0, 3).map((item: any) => ({
            epc: item.epc,
            tagId: item.tagId,
            upc: item.upc,
            description: item.description,
            isVerified: item.isVerified
          }))
        });
        setCurrentStock(mergedCurrentStock);
        // Clear loading when current stock data is received
        if (editModalVisible && loadingModalDataRef.current) {
          setLoadingModalData(false);
        }
      }
      
      if (wsData?.data?.scannedTags) {
        const now = Date.now();
        const currentTime = now;
        
        // Get current stock EPCs to filter out from scanned tags
        // Use currentStock from state (if already set) or from wsData
        const currentStockData = currentStockRef.current.length > 0 ? currentStockRef.current : 
          (wsData?.data?.currentStock || wsData?.data?.existingData || []);
        
        // Normalize EPCs for consistent matching (trim, uppercase)
        const currentStockEpcs = new Set(
          currentStockData
            .map((item: any) => item.epc.trim())
            .filter((epc: any) => epc)
        );
        
        // Also include EPCs that were manually moved to currentStock (via Add Tag button)
        // These are already normalized when added to the ref
        // This ensures they stay filtered out even if WebSocket updates come before state updates
        manuallyMovedToCurrentStockRef.current.forEach((timestamp, epc) => {
          // EPCs in ref are already normalized, but ensure normalization here too
          const normalizedEpc = epc.trim();
          if (normalizedEpc) {
            currentStockEpcs.add(normalizedEpc);
          }
        });
        
        if (!shouldProcessScanBatch(wsData)) {
          console.log('[Edit Reference Stock] Skipping scannedTags update - already processed current scan cycle');
          return;
        }
        
        // Process new scanned tags from WebSocket
        const processedScannedTags = wsData.data.scannedTags
          .filter((item: any) => {
            // Normalize EPC for consistent matching
            const normalizedEpc = item.epc.trim();
            const isInCurrentStock = normalizedEpc && currentStockEpcs.has(normalizedEpc);
            if (isInCurrentStock) {
              console.log('[Edit Reference Stock] Filtering out tag from scannedTags (exists in currentStock or manually moved):', {
                originalEpc: item.epc,
                normalizedEpc: normalizedEpc,
                tagId: item.tagId,
                isManuallyMoved: manuallyMovedToCurrentStockRef.current.has(normalizedEpc) || manuallyMovedToCurrentStockRef.current.has(item.epc)
              });
            }
            return !isInCurrentStock;
          })
          .map((item: any, index: number) => {
            const epc = item.epc;
            
            // Check cache for existing mapped tag
            const cachedTag = mappedTagsCacheRef.current.get(epc);
            
            // Merge current item with cached data to preserve UPC and description
            const mergedItem = {
              ...item,
              // Preserve UPC from cache if current item doesn't have it
              upc: item.upc || cachedTag?.tag?.upc || '',
              // Preserve description from cache if current item doesn't have it
              productDescription: item.productDescription || item.description || cachedTag?.tag?.description || '',
              description: item.productDescription || item.description || cachedTag?.tag?.description || '',
              rssi: item.rssi || cachedTag?.tag?.rssi || ''
            };
            
            // Update cache if we have any mapping (UPC or description)
            if (mergedItem.upc || mergedItem.description) {
              mappedTagsCacheRef.current.set(epc, {
                tag: {
                  ...mergedItem,
                  upc: mergedItem.upc,
                  rssi: mergedItem.rssi,
                  description: mergedItem.description
                },
                timestamp: currentTime
              });
            } else if (cachedTag && (currentTime - cachedTag.timestamp) < TAG_CACHE_DURATION_MS) {
              // If no mapping in current item but cache exists and is valid, use cached data
              mergedItem.upc = cachedTag.tag.upc || mergedItem.upc;
              mergedItem.description = cachedTag.tag.description || mergedItem.description;
              mergedItem.productDescription = cachedTag.tag.description || mergedItem.productDescription;
            }
            
            return {
              ...mergedItem,
              id: `scanned-${epc}-${mergedItem.tagId || index}`,
              key: `scanned-${epc}-${mergedItem.tagId || index}`,
              upc: mergedItem.upc,
              rssi: mergedItem.rssi,
              description: mergedItem.description
            };
          });
        
        // Merge with cached tags that are still valid but not in current scan
        // This ensures tags with product descriptions persist even if they temporarily disappear
        const currentEpcs = new Set(processedScannedTags.map((tag: any) => tag.epc));
        const cachedTagsToAdd: any[] = [];
        
        mappedTagsCacheRef.current.forEach((cached, epc) => {
          // Only add cached tags that:
          // 1. Are not in current scan
          // 2. Are not in current stock (CRITICAL: filter out current stock tags)
          // 3. Have a product description or UPC (any mapping)
          // 4. Are still within cache duration
          if (!currentEpcs.has(epc) && 
              !currentStockEpcs.has(epc) &&
              (cached.tag.description || cached.tag.upc) && 
              (currentTime - cached.timestamp) < TAG_CACHE_DURATION_MS) {
            cachedTagsToAdd.push({
              ...cached.tag,
              id: `scanned-${epc}-cached`,
              key: `scanned-${epc}-cached`,
              isCached: true // Flag to indicate this is from cache
            });
          }
        });
        
        // Combine current tags with cached tags
        const mergedTags = [...processedScannedTags, ...cachedTagsToAdd];
        
        console.log('[Edit Reference Stock] Processing scannedTags with cache:', {
          count: processedScannedTags.length,
          cachedCount: cachedTagsToAdd.length,
          totalCount: mergedTags.length,
          scannedCount: wsData?.data?.scannedCount,
          extraCount: wsData?.data?.extraCount,
          cacheSize: mappedTagsCacheRef.current.size,
          sampleItems: mergedTags.slice(0, 3).map((item: any) => ({
            epc: item.epc,
            tagId: item.tagId,
            upc: item.upc,
            rssi: item.rssi,
            description: item.description,
            isCached: item.isCached || false
          }))
        });
        
        setScannedTags(mergedTags);
        // Clear loading when scanned tags data is received
        if (editModalVisible && loadingModalDataRef.current) {
          setLoadingModalData(false);
        }
      }
      
      // Log additional data if present
      if (wsData?.data?.missingTags || wsData?.data?.extraTags) {
        console.log('[Edit Reference Stock] Additional data in edit-reference-stock-data message:', {
          missingTagsCount: wsData?.data?.missingTags?.length || 0,
          extraTagsCount: wsData?.data?.extraTags?.length || 0
        });
      }
      
      console.log('[Edit Reference Stock] WebSocket data processing completed successfully');
    };
    
    const handleEditRefStockData = (
      wsData: any,
      options?: { trustedSiteZoneDevice?: boolean; source?: string }
    ) => {
      const normalizedWsData =
        wsData?.data?.data ? { ...wsData, data: { ...wsData.data, ...wsData.data.data } } : wsData;

      if (!hasLoggedFirstEditRefStockPayloadRef.current) {
        hasLoggedFirstEditRefStockPayloadRef.current = true;
        console.log('[Edit Reference Stock] FIRST payload received (raw + normalized):', {
          raw: wsData,
          normalized: normalizedWsData,
          source: options?.source || 'unknown'
        });
      }

      console.log('[Edit Reference Stock] WebSocket message received (edit-reference-stock-data):', {
        timestamp: new Date().toISOString(),
        eventName: primaryEventName,
        messageType: normalizedWsData?.type,
        processId: normalizedWsData?.data?.processId,
        deviceId: normalizedWsData?.data?.deviceId,
        currentStockCount: normalizedWsData?.data?.currentStock?.length || 0,
        scannedTagsCount: normalizedWsData?.data?.scannedTags?.length || 0,
        ignoreWebSocketUpdates: ignoreWebSocketUpdatesRef.current,
        timeSinceLastManualUpdate: Date.now() - lastManualUpdateTimeRef.current,
        fullData: normalizedWsData,
        source: options?.source || 'unknown'
      });

      // Accept only batch payloads (aggregated per scan cycle)
      if (!isBatchEditRefStockPayload(normalizedWsData)) {
        console.log('[Edit Reference Stock] Ignoring non-batch edit-reference-stock-data message', {
          messageType: normalizedWsData?.type,
          hasData: !!normalizedWsData?.data,
          dataKeys: normalizedWsData?.data ? Object.keys(normalizedWsData.data) : [],
          note: 'Non-batch payload - expected arrays like currentStock/scannedTags/missingTags/extraTags'
        });
        return;
      }
      
      // Ignore WebSocket updates for 5 seconds after manual add/delete operations
      // This prevents WebSocket messages from overwriting manual UI updates
      const timeSinceLastManualUpdate = Date.now() - lastManualUpdateTimeRef.current;
      if (ignoreWebSocketUpdatesRef.current && timeSinceLastManualUpdate < 5000) {
        console.log('[Edit Reference Stock] Ignoring WebSocket update - recent manual operation (add/delete)', {
          timeSinceLastManualUpdate,
          ignoreFlag: ignoreWebSocketUpdatesRef.current
        });
        return;
      }
      
      // Clear ignore flag if enough time has passed
      if (ignoreWebSocketUpdatesRef.current && timeSinceLastManualUpdate >= 5000) {
        console.log('[Edit Reference Stock] Clearing ignore flag - enough time has passed since manual operation');
        ignoreWebSocketUpdatesRef.current = false;
      }
      
      // CRITICAL: Filter by siteId, zoneId, deviceId, and processId to prevent cross-site/zone contamination
      // Extract siteId and zoneId from WebSocket data (check both data.data and data levels)
      const wsSiteId = normalizedWsData?.data?.siteId || normalizedWsData?.siteId || '';
      const wsZoneId = normalizedWsData?.data?.zoneId || normalizedWsData?.zoneId || '';
      const wsDeviceId = normalizedWsData?.data?.deviceId || normalizedWsData?.deviceId;
      const wsProcessId = normalizedWsData?.data?.processId || normalizedWsData?.processId;
      
      const hasSiteId = Boolean(wsSiteId);
      const hasZoneId = Boolean(wsZoneId);
      const hasDeviceId = Boolean(wsDeviceId);
      const hasProcessId = Boolean(wsProcessId);

      const siteIdMatches = hasSiteId ? wsSiteId === siteId : Boolean(options?.trustedSiteZoneDevice);
      const zoneIdMatches = hasZoneId ? wsZoneId === zoneId : Boolean(options?.trustedSiteZoneDevice);
      const deviceIdMatches = hasDeviceId ? wsDeviceId === deviceId : Boolean(options?.trustedSiteZoneDevice);
      const processIdMatches = options?.trustedSiteZoneDevice
        ? true
        : hasProcessId
          ? wsProcessId === processId
          : false;
      
      // CRITICAL: Require siteId, zoneId, deviceId, AND processId to match
      // This prevents data from other sites/zones/devices/processes from being displayed
      if (siteIdMatches && zoneIdMatches && deviceIdMatches && processIdMatches) {
        console.log('[Edit Reference Stock] WebSocket data matches site/zone/device/process - processing update (throttled)', {
          siteIdMatches: true,
          zoneIdMatches: true,
          deviceIdMatches: true,
          processIdMatches: true,
          receivedSiteId: wsSiteId,
          expectedSiteId: siteId,
          receivedZoneId: wsZoneId,
          expectedZoneId: zoneId,
          receivedDeviceId: wsDeviceId,
          expectedDeviceId: deviceId,
          receivedProcessId: wsProcessId,
          expectedProcessId: processId,
          note: 'Processing Edit Reference Stock data with throttling (max once every 150ms) - all filters match',
          source: options?.source || 'unknown',
          trustedSiteZoneDevice: Boolean(options?.trustedSiteZoneDevice)
        });
        
        // Update cycle counts in real-time from WebSocket event
        updateCycleCountsFromWebSocket(normalizedWsData);
        
        // DO NOT set loading to false here - loading state should remain until API call completes
        // The external solution API call might still be in progress even when WebSocket data arrives
        
        // Apply throttled update
        applyThrottledUpdate(normalizedWsData);
      } else {
        console.log('[Edit Reference Stock] WebSocket data does not match site/zone/device/process - ignoring', {
          receivedSiteId: wsSiteId,
          expectedSiteId: siteId,
          siteIdMatches,
          receivedZoneId: wsZoneId,
          expectedZoneId: zoneId,
          zoneIdMatches,
          receivedDeviceId: wsDeviceId,
          expectedDeviceId: deviceId,
          deviceIdMatches,
          receivedProcessId: wsProcessId,
          expectedProcessId: processId,
          processIdMatches,
          note: 'Message will be ignored - requires siteId, zoneId, deviceId, AND processId to match',
          source: options?.source || 'unknown'
        });
      }
    };

    // Fallback handler for check-reference-stock messages
    // Backend now sends stock data in check-reference-stock messages after backend changes
    // Backend sends: currentStock, missingTags, extraTags, scannedTags, and counts
    const handleCheckRefStockData = (wsData: any) => {
      // Extract data from different possible locations in the message structure
      // Backend sends data in wsData.data (not nested in wsData.data.data)
      const data = wsData?.data?.data || wsData?.data || wsData;
      
      console.log('[Edit Reference Stock] WebSocket message received (check-reference-stock):', {
        timestamp: new Date().toISOString(),
        eventName: checkRefStockEventName,
        messageType: wsData?.type,
        state: wsData?.state,
        processId: data?.processId,
        deviceId: data?.deviceId,
        // Check for new backend data structure
        hasCurrentStock: !!(data?.currentStock),
        hasScannedTags: !!(data?.scannedTags),
        hasMissingTags: !!(data?.missingTags),
        hasExtraTags: !!(data?.extraTags),
        hasExistingData: !!(data?.existingData), // Fallback for old structure
        // Check for counts
        verifiedCount: data?.verifiedCount,
        totalCount: data?.totalCount,
        missingCount: data?.missingCount,
        extraCount: data?.extraCount,
        scannedCount: data?.scannedCount,
        // Show keys to understand structure
        dataKeys: data ? Object.keys(data) : [],
        fullData: wsData,
        extractedData: data,
        note: 'Backend now sends currentStock, missingTags, extraTags, scannedTags, and counts in check-reference-stock messages'
      });
      
      // Ignore WebSocket updates for 5 seconds after manual add/delete operations
      const timeSinceLastManualUpdate = Date.now() - lastManualUpdateTimeRef.current;
      if (ignoreWebSocketUpdatesRef.current && timeSinceLastManualUpdate < 5000) {
        console.log('[Edit Reference Stock] Ignoring check-reference-stock WebSocket update - recent manual operation', {
          timeSinceLastManualUpdate,
          ignoreFlag: ignoreWebSocketUpdatesRef.current
        });
        return;
      }
      
      // Accept only batch payloads (aggregated per scan cycle)
      if (!isBatchEditRefStockPayload(data)) {
        console.log('[Edit Reference Stock] Ignoring non-batch check-reference-stock message', {
          messageType: wsData?.type,
          dataKeys: data ? Object.keys(data) : [],
          note: 'Non-batch payload - expected arrays like currentStock/scannedTags/missingTags/extraTags'
        });
        return;
      }

      // Process if this is for our site/zone/device AND process (don't require modal to be open - process data so it's ready when modal opens)
      const messageSiteId = data?.siteId || '';
      const messageZoneId = data?.zoneId || '';
      const messageDeviceId = data?.deviceId;
      const messageProcessId = data?.processId;
      
      const siteIdMatches = messageSiteId === siteId;
      const zoneIdMatches = messageZoneId === zoneId;
      const deviceIdMatches = messageDeviceId === deviceId;
      const processIdMatches = messageProcessId === processId;
      
      // CRITICAL: Require siteId, zoneId, deviceId, AND processId to match
      // This prevents data from other sites/zones/devices/processes from being displayed
      if (siteIdMatches && zoneIdMatches && deviceIdMatches && processIdMatches) {
        console.log('[Edit Reference Stock] Processing check-reference-stock message with stock data', {
          siteIdMatches: true,
          zoneIdMatches: true,
          deviceIdMatches: true,
          processIdMatches: true,
          receivedSiteId: messageSiteId,
          expectedSiteId: siteId,
          receivedZoneId: messageZoneId,
          expectedZoneId: zoneId,
          receivedDeviceId: messageDeviceId,
          expectedDeviceId: deviceId,
          receivedProcessId: messageProcessId,
          expectedProcessId: processId
        });
        
        // Update cycle counts in real-time from WebSocket event
        updateCycleCountsFromWebSocket(wsData);
        
        // Apply throttled update if we have actual data arrays
        if (data?.currentStock || data?.existingData || data?.scannedTags) {
          // Create a wsData-like structure for throttling function
          const wsDataForThrottle = {
            data: {
              currentStock: data?.currentStock,
              existingData: data?.existingData,
              scannedTags: data?.scannedTags,
              verifiedCount: data?.verifiedCount,
              totalCount: data?.totalCount,
              missingCount: data?.missingCount,
              scannedCount: data?.scannedCount,
              extraCount: data?.extraCount,
              missingTags: data?.missingTags,
              extraTags: data?.extraTags
            }
          };
          // Use throttled update to limit UI updates
          applyThrottledUpdate(wsDataForThrottle);
        }
        
        let dataProcessed = false;
        
        // Check if we have data (actual processing happens in throttled function)
        if (data?.currentStock || data?.existingData || data?.scannedTags) {
          dataProcessed = true;
        }
        
        // Log additional data if present (for debugging/info)
        if (data?.missingTags || data?.extraTags) {
          console.log('[Edit Reference Stock] Additional data in message:', {
            missingTagsCount: data?.missingTags?.length || 0,
            extraTagsCount: data?.extraTags?.length || 0,
            note: 'missingTags and extraTags are for reference - main data is in currentStock and scannedTags'
          });
        }
        
        // Log summary
        if (dataProcessed) {
          console.log('[Edit Reference Stock] Data successfully processed from check-reference-stock message:', {
            state: wsData?.state,
            currentStockCount: (data?.currentStock || data?.existingData)?.length || 0,
            scannedTagsCount: data?.scannedTags?.length || 0,
            siteIdMatches,
            zoneIdMatches,
            deviceIdMatches,
            processIdMatches,
            verifiedCount: data?.verifiedCount,
            totalCount: data?.totalCount,
            missingCount: data?.missingCount,
            extraCount: data?.extraCount,
            scannedCount: data?.scannedCount,
            note: 'Data should now be visible in the Edit Reference Stock modal'
          });
        } else {
          // Log more details when no data is found
          // DATA MISSING ANALYSIS: Based on logs, check-reference-stock messages at certain states (Notify, Wait) 
          // only contain counts (scannedCount, verifiedCount) but NOT the actual arrays (currentStock, scannedTags).
          // This happens because:
          // 1. At 'Notify' state: Backend sends counts but arrays are sent separately via edit-reference-stock-data
          // 2. At 'Wait' state: Backend sends state update but no stock data (process is waiting)
          // Solution: Rely on edit-reference-stock-data and activity-log messages for actual tag arrays
          console.log('[Edit Reference Stock] No stock data found in check-reference-stock message:', {
            state: wsData?.state,
            messageType: wsData?.type,
            hasCounts: !!(data?.scannedCount !== undefined || data?.verifiedCount !== undefined || data?.totalCount !== undefined),
            scannedCount: data?.scannedCount,
            verifiedCount: data?.verifiedCount,
            totalCount: data?.totalCount,
            missingCount: data?.missingCount,
            dataKeys: data ? Object.keys(data) : [],
            note: 'Message contains counts but not arrays. Arrays are sent via edit-reference-stock-data or activity-log messages. This is expected behavior at Notify/Wait states.',
            expectedDataSources: [
              'edit-reference-stock-data messages (primary source for currentStock and scannedTags arrays)',
              'activity-log messages with state="TrackNTrace" (contains existingData and scannedTags arrays)'
            ]
          });
        }
      } else {
        console.log('[Edit Reference Stock] check-reference-stock message ignored - siteId/zoneId/deviceId/processId do not match:', {
          receivedSiteId: messageSiteId,
          expectedSiteId: siteId,
          siteIdMatches,
          receivedZoneId: messageZoneId,
          expectedZoneId: zoneId,
          zoneIdMatches,
          receivedDeviceId: messageDeviceId,
          expectedDeviceId: deviceId,
          deviceIdMatches,
          receivedProcessId: messageProcessId,
          expectedProcessId: processId,
          processIdMatches,
          note: 'Message will be ignored - requires siteId, zoneId, deviceId, AND processId to match'
        });
      }
    };

    // Register primary listener (with Check Reference Stock processId - execution processId)
    socket.on(primaryEventName, (wsData: any) =>
      handleEditRefStockData(wsData, { trustedSiteZoneDevice: false, source: 'primary' })
    );
    editRefStockListenerRef.current = handleEditRefStockData;
    
    // CRITICAL: Also listen with Set Reference Stock processId (storage processId) if available
    // Backend stores state with Set Reference Stock processId and might send WebSocket messages with that processId
    if (setReferenceStockEventName) {
      console.log('[Edit Reference Stock] Registering listener with Set Reference Stock processId:', setReferenceStockEventName);
      socket.on(setReferenceStockEventName, (wsData: any) =>
        handleEditRefStockData(wsData, { trustedSiteZoneDevice: false, source: 'setReferenceStock' })
      );
    }
    
    // Register fallback listener for check-reference-stock
    socket.on(checkRefStockEventName, handleCheckRefStockData);
    
    // CRITICAL: Listen to base event (without processId in event name) to catch messages with ANY processId
    // Backend might send with Set Reference Stock processId (storage) in the event name:
    // edit-reference-stock-data-{deviceId}-{Set Reference Stock processId}
    // But we're listening with Check Reference Stock processId (execution):
    // edit-reference-stock-data-{deviceId}-{Check Reference Stock processId}
    // The base event listener will catch messages regardless of processId in the event name
    const handleBaseEditRefStock = (wsData: any) => {
      const data = wsData?.data?.data || wsData?.data || wsData;
      // CRITICAL: Filter by siteId, zoneId, deviceId, and processId to ensure we only process messages for our context
      const wsSiteId = data?.siteId || wsData?.siteId || '';
      const wsZoneId = data?.zoneId || wsData?.zoneId || '';
      const wsDeviceId = data?.deviceId || wsData?.deviceId;
      const wsProcessId = data?.processId || wsData?.processId;
      
      const siteIdMatches = wsSiteId === siteId;
      const zoneIdMatches = wsZoneId === zoneId;
      const deviceIdMatches = wsDeviceId === deviceId;
      const processIdMatches = wsProcessId === processId;
      
      if (siteIdMatches && zoneIdMatches && deviceIdMatches && processIdMatches) {
        console.log('[Edit Reference Stock] WebSocket event received (base edit-reference-stock-data event - catches messages with any processId):', {
          timestamp: new Date().toISOString(),
          eventName: WebsocketKey.EDIT_REFERENCE_STOCK_DATA,
          messageType: wsData?.type,
          receivedSiteId: wsSiteId,
          expectedSiteId: siteId,
          siteIdMatches: true,
          receivedZoneId: wsZoneId,
          expectedZoneId: zoneId,
          zoneIdMatches: true,
          receivedDeviceId: wsDeviceId,
          expectedDeviceId: deviceId,
          deviceIdMatches: true,
          receivedProcessId: wsProcessId,
          expectedProcessId: processId,
          processIdMatches: true,
          hasCurrentStock: !!data?.currentStock,
          hasScannedTags: !!data?.scannedTags,
          note: 'This listener catches messages regardless of processId in event name - backend might use Set Reference Stock processId. All filters match.',
          fullData: wsData
        });
        // Process the message - siteId, zoneId, deviceId, and processId all match, so it's for our context
        handleEditRefStockData(wsData, { trustedSiteZoneDevice: false, source: 'base' });
      } else {
        console.log('[Edit Reference Stock] Base event message ignored - siteId/zoneId/deviceId/processId mismatch', {
          receivedSiteId: wsSiteId,
          expectedSiteId: siteId,
          siteIdMatches,
          receivedZoneId: wsZoneId,
          expectedZoneId: zoneId,
          zoneIdMatches,
          receivedDeviceId: wsDeviceId,
          expectedDeviceId: deviceId,
          deviceIdMatches,
          receivedProcessId: wsProcessId,
          expectedProcessId: processId,
          processIdMatches,
          note: 'Message will be ignored - requires siteId, zoneId, deviceId, AND processId to match'
        });
      }
    };
    
    // CRITICAL: Register site-zone-device event listener - backend emits to edit-reference-stock-data-{siteId}-{zoneId}-{deviceId}
    // This is the most reliable pattern as it doesn't depend on processId matching
    socket.on(siteZoneDeviceEventName, (wsData: any) =>
      handleEditRefStockData(wsData, { trustedSiteZoneDevice: true, source: 'siteZoneDevice' })
    );
    
    // Register base event listener - this will catch messages with ANY processId in the event name
    socket.on(baseEventName, handleBaseEditRefStock);
    
    // FALLBACK: Listen to activity-log messages - backend might be sending EditReferenceStockData via activity-log
    // The backend sends TrackNTrace node output via activity-log, which might contain scannedTags and existingData
    // New pattern: activity-log-{siteId}-{zoneId}-{deviceId}-{processId}
    const activityLogEventName = `activity-log-${siteId}-${zoneId}-${deviceId}-${processId}`;
    const handleActivityLog = (wsData: any) => {
      // Log ALL activity-log messages for debugging
      const messageDeviceId = wsData?.data?.deviceId || wsData?.deviceId;
      const messageSiteId = wsData?.data?.siteId || wsData?.siteId || '';
      const messageZoneId = wsData?.data?.zoneId || wsData?.zoneId || '';
      const messageState = wsData?.state || wsData?.data?.state;
      
      console.log('[Edit Reference Stock] activity-log message received:', {
        timestamp: new Date().toISOString(),
        eventName: 'activity-log',
        state: messageState,
        processId: wsData?.data?.processId || wsData?.processId,
        deviceId: messageDeviceId,
        expectedDeviceId: deviceId,
        deviceIdMatches: messageDeviceId === deviceId,
        hasData: !!wsData?.data,
        dataKeys: wsData?.data ? Object.keys(wsData.data) : [],
        hasScannedTags: !!(wsData?.data?.scannedTags || wsData?.data?.data?.scannedTags || wsData?.scannedTags),
        hasExistingData: !!(wsData?.data?.existingData || wsData?.data?.data?.existingData || wsData?.existingData),
        hasCurrentStock: !!(wsData?.data?.currentStock || wsData?.data?.data?.currentStock || wsData?.currentStock),
        fullData: wsData
      });
      
      // Process if this is for our device OR our processId (processId match is more reliable)
      // Don't require editModalVisible - process data even if modal isn't open yet, so it's ready when user opens it
      // Extract data from different possible locations in the message structure first
      const data = wsData?.data?.data || wsData?.data || wsData;
      const messageProcessId = wsData?.data?.processId || wsData?.processId || data?.processId;
      const processIdMatches = messageProcessId === processId;
      const deviceIdMatches = messageDeviceId === deviceId;
      const siteIdMatches = messageSiteId === siteId;
      const zoneIdMatches = messageZoneId === zoneId;
      
      // Ignore WebSocket updates for 5 seconds after manual add/delete operations
      const timeSinceLastManualUpdate = Date.now() - lastManualUpdateTimeRef.current;
      if (ignoreWebSocketUpdatesRef.current && timeSinceLastManualUpdate < 5000) {
        console.log('[Edit Reference Stock] Ignoring activity-log WebSocket update - recent manual operation', {
          timeSinceLastManualUpdate,
          ignoreFlag: ignoreWebSocketUpdatesRef.current
        });
        return;
      }

      // Accept only batch payloads (aggregated per scan cycle)
      if (!isBatchEditRefStockPayload(data)) {
        console.log('[Edit Reference Stock] Ignoring non-batch activity-log message', {
          messageType: wsData?.type,
          dataKeys: data ? Object.keys(data) : [],
          note: 'Non-batch payload - expected arrays like currentStock/scannedTags/missingTags/extraTags'
        });
        return;
      }
      
      // CRITICAL: Require siteId, zoneId, deviceId AND processId to match
      // This prevents data from other sites/zones/devices/processes from being displayed
      // Even if they share the same processId, different sites/zones/devices should have separate data
      if (siteIdMatches && zoneIdMatches && deviceIdMatches && processIdMatches) {
        
        console.log('[Edit Reference Stock] Processing activity-log message:', {
          timestamp: new Date().toISOString(),
          eventName: 'activity-log',
          state: messageState,
          processId: messageProcessId,
          deviceId: messageDeviceId,
          expectedProcessId: processId,
          expectedDeviceId: deviceId,
          processIdMatches,
          deviceIdMatches,
          matchReason: 'Both deviceId and processId match',
          hasScannedTags: !!(data?.scannedTags),
          hasExistingData: !!(data?.existingData),
          hasCurrentStock: !!(data?.currentStock),
          scannedTagsCount: data?.scannedTags?.length || 0,
          existingDataCount: data?.existingData?.length || 0,
          currentStockCount: data?.currentStock?.length || 0,
          editModalVisible: editModalVisible,
          extractedData: data,
          note: 'Processing because both deviceId and processId match'
        });
        
        // Update cycle counts in real-time from WebSocket event
        updateCycleCountsFromWebSocket(wsData);
        
        // Process the data if it contains scannedTags, existingData, or currentStock
        // Backend now sends: currentStock, missingTags, extraTags, scannedTags, and counts
        if (data?.scannedTags || data?.existingData || data?.currentStock) {
          console.log('[Edit Reference Stock]  Processing activity-log message as EditReferenceStockData');
          
          // Map currentStock (new) or existingData (fallback) to currentStock format
          if (data?.currentStock || data?.existingData) {
            const stockData = data.currentStock || data.existingData;
            const isInitialEmptyLoad =
              currentStockRef.current.length === 0 &&
              scannedTagsRef.current.length === 0 &&
              loadingModalDataRef.current;
            const shouldUpdateCurrentStock = stockData.length > 0 || isInitialEmptyLoad;
            if (!shouldUpdateCurrentStock) {
              console.log('[Edit Reference Stock] Skipping currentStock update (activity-log) - empty payload would overwrite existing data', {
                stockDataCount: stockData.length,
                currentStockCount: currentStockRef.current.length,
                scannedTagsCount: scannedTagsRef.current.length,
                loadingModalData: loadingModalDataRef.current
              });
            } else {
            
            // Filter out tags that were manually removed from currentStock (via Delete Tag button)
            // This ensures they stay filtered out even if WebSocket updates come in
            // Normalize EPCs for consistent matching
            const filteredStockData = stockData.filter((item: any) => {
              const normalizedEpc = item.epc;
              // Check both normalized and original EPC in the ref (ref stores normalized)
              const isManuallyRemoved = normalizedEpc && (
                manuallyRemovedFromCurrentStockRef.current.has(normalizedEpc) ||
                manuallyRemovedFromCurrentStockRef.current.has(item.epc)
              );
              if (isManuallyRemoved) {
                console.log('[Edit Reference Stock] Filtering out tag from currentStock (manually removed) - activity-log:', {
                  originalEpc: item.epc,
                  normalizedEpc: normalizedEpc,
                  tagId: item.tagId
                });
              }
              return !isManuallyRemoved;
            });
            
            const processedCurrentStock = filteredStockData.map((item: any, index: number) => ({
              ...item,
              id: `current-${item.epc}-${item.tagId || index}`,
              key: `current-${item.epc}-${item.tagId || index}`,
              description: item.productDescription || item.description || '', // Backend automatically enriches with productDescription
              isVerified: item.isVerified !== undefined ? item.isVerified : true
            }));
            const mergedCurrentStock = applyPendingActionsToCurrentStock(processedCurrentStock);
            console.log('[Edit Reference Stock]  Processing currentStock from activity-log:', {
              count: mergedCurrentStock.length,
              originalCount: stockData.length,
              filteredOutCount: stockData.length - processedCurrentStock.length,
              source: data.currentStock ? 'currentStock' : 'existingData (fallback)',
              verifiedCount: data?.verifiedCount,
              totalCount: data?.totalCount,
              missingCount: data?.missingCount,
              scannedCount: data?.scannedCount,
              manuallyRemovedCount: manuallyRemovedFromCurrentStockRef.current.size,
              sampleItems: mergedCurrentStock.slice(0, 3)
            });
            setCurrentStock(mergedCurrentStock);
            // Clear loading when current stock data is received
            if (editModalVisible && loadingModalDataRef.current) {
              setLoadingModalData(false);
            }
            }
          }
          
          // Process scannedTags with cache support
          if (data?.scannedTags) {
            const now = Date.now();
            const currentTime = now;
            
            // Get current stock EPCs to filter out from scanned tags
            // Use currentStock from state (if already set) or from data
            const currentStockData = currentStockRef.current.length > 0 ? currentStockRef.current : 
              (data?.currentStock || data?.existingData || []);
            
            // Normalize EPCs for consistent matching (trim, uppercase)
            const currentStockEpcs = new Set(
              currentStockData
                .map((item: any) => item.epc)
                .filter((epc: any) => epc)
            );
            
            // Also include EPCs that were manually moved to currentStock (via Add Tag button)
            // These are already normalized when added to the ref
            // This ensures they stay filtered out even if WebSocket updates come before state updates
            manuallyMovedToCurrentStockRef.current.forEach((timestamp, epc) => {
              // EPCs in ref are already normalized, but ensure normalization here too
              const normalizedEpc = epc;
              if (normalizedEpc) {
                currentStockEpcs.add(normalizedEpc);
              }
            });
            
            if (!shouldProcessScanBatch(wsData)) {
              console.log('[Edit Reference Stock] Skipping scannedTags update (activity-log) - already processed current scan cycle');
              return;
            }
            
            // Process new scanned tags from activity-log
            const processedScannedTags = data.scannedTags
              .filter((item: any) => {
                // Normalize EPC for consistent matching
                const normalizedEpc = item.epc;
                const isInCurrentStock = normalizedEpc && currentStockEpcs.has(normalizedEpc);
                if (isInCurrentStock) {
                  console.log('[Edit Reference Stock] Filtering out tag from scannedTags (exists in currentStock or manually moved) - activity-log:', {
                    originalEpc: item.epc,
                    normalizedEpc: normalizedEpc,
                    tagId: item.tagId,
                    isManuallyMoved: manuallyMovedToCurrentStockRef.current.has(normalizedEpc) || manuallyMovedToCurrentStockRef.current.has(item.epc)
                  });
                }
                return !isInCurrentStock;
              })
              .map((item: any, index: number) => {
                const epc = item.epc;
                
                // Check cache for existing mapped tag
                const cachedTag = mappedTagsCacheRef.current.get(epc);
                
                // Merge current item with cached data to preserve UPC and description
                const mergedItem = {
                  ...item,
                  // Preserve UPC from cache if current item doesn't have it
                  upc: item.upc || cachedTag?.tag?.upc || '',
                  // Preserve description from cache if current item doesn't have it
                  productDescription: item.productDescription || item.description || cachedTag?.tag?.description || '',
                  description: item.productDescription || item.description || cachedTag?.tag?.description || '',
                  rssi: item.rssi || cachedTag?.tag?.rssi || ''
                };
                
                // Update cache if we have any mapping (UPC or description)
                if (mergedItem.upc || mergedItem.description) {
                  mappedTagsCacheRef.current.set(epc, {
                    tag: {
                      ...mergedItem,
                      upc: mergedItem.upc,
                      rssi: mergedItem.rssi,
                      description: mergedItem.description
                    },
                    timestamp: currentTime
                  });
                } else if (cachedTag && (currentTime - cachedTag.timestamp) < TAG_CACHE_DURATION_MS) {
                  // If no mapping in current item but cache exists and is valid, use cached data
                  mergedItem.upc = cachedTag.tag.upc || mergedItem.upc;
                  mergedItem.description = cachedTag.tag.description || mergedItem.description;
                  mergedItem.productDescription = cachedTag.tag.description || mergedItem.productDescription;
                }
                
                return {
                  ...mergedItem,
                  id: `scanned-${epc}-${mergedItem.tagId || index}`,
                  key: `scanned-${epc}-${mergedItem.tagId || index}`,
                  upc: mergedItem.upc,
                  rssi: mergedItem.rssi,
                  description: mergedItem.description
                };
              });
            
            // Merge with cached tags that are still valid but not in current scan
            const currentEpcs = new Set(processedScannedTags.map((tag: any) => tag.epc));
            const cachedTagsToAdd: any[] = [];
            
            mappedTagsCacheRef.current.forEach((cached, epc) => {
              // Only add cached tags that:
              // 1. Are not in current scan
              // 2. Are not in current stock (CRITICAL: filter out current stock tags)
              // 3. Have a product description or UPC (any mapping)
              // 4. Are still within cache duration
              if (!currentEpcs.has(epc) && 
                  !currentStockEpcs.has(epc) &&
                  (cached.tag.description || cached.tag.upc) && 
                  (currentTime - cached.timestamp) < TAG_CACHE_DURATION_MS) {
                cachedTagsToAdd.push({
                  ...cached.tag,
                  id: `scanned-${epc}-cached`,
                  key: `scanned-${epc}-cached`,
                  isCached: true
                });
              }
            });
            
            // Combine current tags with cached tags
            const mergedTags = [...processedScannedTags, ...cachedTagsToAdd];
            
            console.log('[Edit Reference Stock] Processing scannedTags from activity-log with cache:', {
              count: processedScannedTags.length,
              cachedCount: cachedTagsToAdd.length,
              totalCount: mergedTags.length,
              scannedCount: data?.scannedCount,
              extraCount: data?.extraCount,
              sampleItems: mergedTags.slice(0, 3)
            });
            setScannedTags(mergedTags);
            // Clear loading when scanned tags data is received
            if (editModalVisible && loadingModalDataRef.current) {
              setLoadingModalData(false);
            }
          }
          
          // Log additional data if present
          if (data?.missingTags || data?.extraTags) {
            console.log('[Edit Reference Stock] Additional data in activity-log message:', {
              missingTagsCount: data?.missingTags?.length || 0,
              extraTagsCount: data?.extraTags?.length || 0
            });
          }
        } else {
          console.log('[Edit Reference Stock] activity-log message received but no scannedTags, existingData, or currentStock found in data structure');
        }
      } else {
        console.log('[Edit Reference Stock] activity-log message ignored - siteId/zoneId/deviceId/processId do not match:', {
          receivedSiteId: messageSiteId,
          expectedSiteId: siteId,
          siteIdMatches,
          receivedZoneId: messageZoneId,
          expectedZoneId: zoneId,
          zoneIdMatches,
          receivedDeviceId: messageDeviceId,
          expectedDeviceId: deviceId,
          deviceIdMatches,
          receivedProcessId: messageProcessId,
          expectedProcessId: processId,
          processIdMatches,
          note: 'Message will be ignored - requires siteId, zoneId, deviceId, AND processId to match'
        });
      }
    };
    
    // Register activity-log listener with filtered event name pattern
    socket.on(activityLogEventName, handleActivityLog);
    // Also listen to base activity-log event as fallback
    socket.on('activity-log', handleActivityLog);
    
    // DEBUG: Log socket connection status to verify WebSocket is connected
    console.log('[Edit Reference Stock] DEBUG: Socket connection status:', {
      connected: socket?.connected,
      id: socket?.id,
      transport: socket?.io?.engine?.transport?.name,
      note: 'If no messages are received, verify backend is sending EditReferenceStockData messages to one of the event names we are listening to'
    });
    
    // SUMMARY: Log what we're listening for
    console.log('[Edit Reference Stock] SUMMARY: WebSocket listeners configured:', {
      timestamp: new Date().toISOString(),
      listeningTo: [
        `1. ${primaryEventName} (primary - edit-reference-stock-data with site-zone-device-process pattern)`,
        setReferenceStockEventName ? `2. ${setReferenceStockEventName} (Set Reference Stock processId with site-zone-device-process pattern)` : '2. Set Reference Stock processId listener: NOT SET (setReferenceStockProcessId is null)',
        `3. ${siteZoneDeviceEventName} (site-zone-device pattern - without processId)`,
        `4. ${baseEventName} (base event - catches all processIds)`,
        `5. ${checkRefStockEventName} (check-reference-stock fallback with site-zone-device-process pattern)`,
        `6. ${activityLogEventName} (activity-log with site-zone-device-process pattern)`,
        `7. activity-log (base activity-log event as fallback)`
      ],
      siteId: siteId,
      zoneId: zoneId,
      deviceId: deviceId,
      processId: processId,
      note: 'Backend emits to filtered pattern: {type}-{siteId}-{zoneId}-{deviceId}-{processId}. Listening to primary pattern, Set Reference Stock processId pattern, site-zone-device pattern, and base event.',
      expectedDataLocation: 'activity-log messages with state="TrackNTrace" should contain scannedTags and existingData in wsData.data'
    });
    
    console.log('[Edit Reference Stock] WebSocket listeners registered successfully', {
      primaryEventName: primaryEventName,
      setReferenceStockEventName: setReferenceStockEventName,
      siteZoneDeviceEventName: siteZoneDeviceEventName,
      baseEventName: baseEventName,
      fallbackEventName: checkRefStockEventName,
      siteId: siteId,
      zoneId: zoneId,
      deviceId: deviceId,
      processId: processId,
      timestamp: new Date().toISOString()
    });

    return () => {
      socket.off('connect', handleSocketConnect);
      socket.off('connect_error', handleSocketConnectError);

      console.log('[Edit Reference Stock] Cleaning up WebSocket listeners', {
        primaryEventName: primaryEventName,
        setReferenceStockEventName: setReferenceStockEventName,
        siteZoneDeviceEventName: siteZoneDeviceEventName,
        baseEventName: baseEventName,
        fallbackEventName: checkRefStockEventName,
        timestamp: new Date().toISOString()
      });
      if (editRefStockListenerRef.current) {
        socket.off(primaryEventName, editRefStockListenerRef.current);
      }
      if (setReferenceStockEventName) {
        socket.off(setReferenceStockEventName, editRefStockListenerRef.current);
      }
      socket.off(siteZoneDeviceEventName, editRefStockListenerRef.current);
      socket.off(checkRefStockEventName, handleCheckRefStockData);
      socket.off(baseEventName, handleBaseEditRefStock);
      socket.off(activityLogEventName, handleActivityLog);
      socket.off('activity-log', handleActivityLog);
      
      // Cleanup throttling timeout
      if (throttlingTimeoutRef.current) {
        clearTimeout(throttlingTimeoutRef.current);
        throttlingTimeoutRef.current = null;
      }
      
      // Cleanup tag cache interval
      if (tagCacheCleanupIntervalRef.current) {
        clearInterval(tagCacheCleanupIntervalRef.current);
        tagCacheCleanupIntervalRef.current = null;
      }
      
      // Clear cache when modal closes
      if (!editModalVisible) {
        mappedTagsCacheRef.current.clear();
      }
    };
  }, [data?.data?.deviceId, data?.data?.processId, editModalVisible, setReferenceStockProcessId]);
  
  // Periodic cleanup of expired cache entries
  useEffect(() => {
    if (!editModalVisible) {
      // Clear cache when modal is not visible
      mappedTagsCacheRef.current.clear();
      if (tagCacheCleanupIntervalRef.current) {
        clearInterval(tagCacheCleanupIntervalRef.current);
        tagCacheCleanupIntervalRef.current = null;
      }
      return;
    }
    
    // Set up periodic cleanup every 30 seconds
    tagCacheCleanupIntervalRef.current = setInterval(() => {
      const now = Date.now();
      let expiredCount = 0;
      
      mappedTagsCacheRef.current.forEach((cached, epc) => {
        if ((now - cached.timestamp) >= TAG_CACHE_DURATION_MS) {
          mappedTagsCacheRef.current.delete(epc);
          expiredCount++;
        }
      });
      
      if (expiredCount > 0) {
        console.log(`[Edit Reference Stock] Cleaned up ${expiredCount} expired cached tags`);
      }
    }, 30000); // Clean up every 30 seconds
    
    return () => {
      if (tagCacheCleanupIntervalRef.current) {
        clearInterval(tagCacheCleanupIntervalRef.current);
        tagCacheCleanupIntervalRef.current = null;
      }
    };
  }, [editModalVisible]);

  // Helper function to check if process is actually running (even if start API failed)
  const checkIfProcessIsActive = async (processId: string, deviceId: string): Promise<boolean> => {
    try {
      // Refetch active processes to get the latest data
      await refetchActiveProcesses();
      
      // Use the activeProcessesData from the query (it will be updated after refetch)
     
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const activeProcesses = activeProcessesData?.result || [];
      
      // Check if process is active by matching processId and deviceId
      // Also check for Set Reference Stock processId (backend stores state with different processId)
      const isActive = activeProcesses.some(
        (p: any) => {
          const processIdMatches = p.processId === processId || p.processId === data?.data?.processId;
          const deviceIdMatches = p.deviceId === deviceId;
          return processIdMatches && deviceIdMatches;
        }
      );
      
      console.log('Edit Reference Stock: Checking if process is active:', {
        requestedProcessId: processId,
        requestedDeviceId: deviceId,
        isActive,
        activeProcessesCount: activeProcesses.length,
        matchingProcesses: activeProcesses.filter((p: any) => p.deviceId === deviceId),
        allActiveProcessIds: activeProcesses.map((p: any) => ({ processId: p.processId, deviceId: p.deviceId, workflowName: p.workflowName }))
      });
      return isActive;
    } catch (err) {
      console.error('Edit Reference Stock: Error checking active processes:', err);
      // Fallback to using cached data if refetch fails
      const activeProcesses = activeProcessesData?.result || [];
      const isActive = activeProcesses.some(
        (p: any) => {
          const processIdMatches = p.processId === processId || p.processId === data?.data?.processId;
          const deviceIdMatches = p.deviceId === deviceId;
          return processIdMatches && deviceIdMatches;
        }
      );
      return isActive;
    }
  };

  // Handle Edit Reference Stock button click - execute ERS workflow
  const handleEditReferenceStockClick = async () => {
    console.log('========================================');
    console.log('[PROCESS START] Method: EDIT REFERENCE STOCK BUTTON');
    console.log('========================================');
    
    if (!data?.data?.processId || !data?.data?.deviceId) {
      message.warning('Process or device information is missing');
      return;
    }

    const socketReady = await ensureSocketConnected();
    if (!socketReady) {
      message.error('WebSocket not connected. Please try again after the connection is restored.');
      console.error('[Edit Reference Stock] Aborting start - socket not connected');
      return;
    }

    console.log('[PROCESS START] EDIT REFERENCE STOCK BUTTON - Button clicked', {
      method: 'EDIT_REFERENCE_STOCK_BUTTON',
      processId: data?.data?.processId,
      deviceId: data?.data?.deviceId,
      processName: data?.data?.processName,
      siteId: data?.data?.siteId,
      zoneId: data?.data?.zoneId,
      tenantId: data?.data?.tenantId || tenantId,
      userId: user?.userId,
      timestamp: new Date().toISOString()
    });

    // Show progress modal immediately when button is clicked
    setErsProgressModalVisible(true);
    setErsProgress(0);
    setErsProgressStep('Initializing...');

    // Update progress: Checking device status
    setErsProgress(25);
    setErsProgressStep('Checking device status...');

    //  Check device status before attempting to start Edit Reference Stock workflow
    
    const deviceStatus = mergedDeviceStatus?.[0]?.status;
    const isQueryLoading = getDeviceStatus?.isLoading;
    const isQuerySkipped = getDeviceStatus?.isUninitialized || !getDeviceStatus;
    
   
    let directApiDeviceStatus: string | undefined = undefined;
    if (!deviceStatus && getDeviceStatus?.data?.result) {
      const apiStatus = getDeviceStatus.data.result;
      
      // Try to find by MAC address first
      if (deviceMacAddress) {
        const matchingDevice = apiStatus.find((d: any) => {
          const statusMac = d?.deviceMac?.toUpperCase()?.trim();
          return statusMac === deviceMacAddress;
        });
        if (matchingDevice) {
          directApiDeviceStatus = matchingDevice?.status;
          console.log('[Edit Reference Stock] Found device status directly from API response by MAC (bypassing filter)', {
            deviceMac: deviceMacAddress,
            status: directApiDeviceStatus,
            deviceId: data?.data?.deviceId
          });
        }
      }
      
      // If MAC matching failed, try to find by deviceId as fallback
      if (!directApiDeviceStatus && data?.data?.deviceId) {
        const matchingDevice = apiStatus.find((d: any) => {
          return d?.deviceId === data.data.deviceId || 
                 (d?.processObject?.deviceId === data.data.deviceId);
        });
        if (matchingDevice) {
          directApiDeviceStatus = matchingDevice?.status;
          console.log('[Edit Reference Stock] Found device status directly from API response by deviceId (bypassing filter)', {
            deviceId: data?.data?.deviceId,
            status: directApiDeviceStatus,
            deviceMac: matchingDevice?.deviceMac
          });
        }
      }
    }
    
    // Use mergedDeviceStatus first, fallback to direct API check
    const effectiveDeviceStatus = deviceStatus || directApiDeviceStatus;
    
    // Log device status for debugging
    console.log('[Edit Reference Stock] Device status check:', {
      deviceStatus: effectiveDeviceStatus,
      deviceStatusSource: deviceStatus ? 'mergedDeviceStatus' : (directApiDeviceStatus ? 'directApiResponse' : 'not found'),
        deviceId: data?.data?.deviceId,
        deviceName: data?.data?.deviceName,
      deviceMac: deviceMacAddress,
      deviceMacSource: data?.data?.macAddress || data?.data?.deviceMac ? 'data.data' : (data?.data?.deviceName?.match(/\[([0-9a-fA-F]{2}(?::[0-9a-fA-F]{2}){5})\]/i) ? 'deviceName' : (deviceDataQuery?.data?.result?.deviceData ? 'deviceDataQuery' : 'not found')),
      mergedDeviceStatusLength: mergedDeviceStatus?.length || 0,
      mergedDeviceStatus: mergedDeviceStatus,
      apiDeviceStatusFilteredLength: apiDeviceStatusFiltered?.length || 0,
      wsDeviceStatusLength: wsDeviceStatus?.length || 0,
      directApiDeviceStatus: directApiDeviceStatus,
        isQueryLoading,
        isQuerySkipped,
      queryDataLength: getDeviceStatus?.data?.result?.length || 0
    });
    
    // Check if device is explicitly offline/disconnected
    // Only block workflow if device status is explicitly "offline" or "disconnected"
    const normalizedStatus = effectiveDeviceStatus?.toLowerCase()?.trim();
    const isDeviceOffline = normalizedStatus === "offline" || 
                           normalizedStatus === "disconnected" ||
                           effectiveDeviceStatus === "offline" || 
                           effectiveDeviceStatus === "disconnected";
    
   
    if (!isQueryLoading && !isQuerySkipped) {
      // Only block if we have confirmed the device is explicitly offline/disconnected
      if (effectiveDeviceStatus && isDeviceOffline) {
        console.error('Edit Reference Stock: Cannot start workflow - Keonn reader is offline/unreachable', {
          deviceStatus: effectiveDeviceStatus,
          normalizedStatus,
          deviceId: data?.data?.deviceId,
          deviceName: data?.data?.deviceName,
          deviceMac: deviceMacAddress,
          deviceMacSource: data?.data?.macAddress || data?.data?.deviceMac ? 'data.data' : (data?.data?.deviceName?.match(/\[([0-9a-fA-F]{2}(?::[0-9a-fA-F]{2}){5})\]/i) ? 'deviceName' : (deviceDataQuery?.data?.result?.deviceData ? 'deviceDataQuery' : 'not found')),
          isQueryLoading,
          isQuerySkipped,
          isDeviceOffline,
          queryData: getDeviceStatus?.data,
          mergedDeviceStatus,
          apiDeviceStatusFiltered,
          wsDeviceStatus,
          directApiDeviceStatus,
          note: 'Device status is explicitly offline/disconnected - blocking workflow'
        });
        
        // Show error modal with the specified error message
      Modal.error({
        title: 'Device Connection Error',
          content: 'Please connect to the keonn reader to edit the Reference stock',
        okText: 'OK',
        onOk: () => {
          console.log('Edit Reference Stock: User acknowledged device connection error');
        }
      });
      
        setErsProgressModalVisible(false); // Close progress modal on error
        setErsProgress(0);
        setErsProgressStep('');
      return;
    }
      // If device status is not explicitly offline, allow workflow to proceed (backend will validate)
      
    } else if (isQueryLoading) {
      console.log('Edit Reference Stock: Device status query is loading - waiting for status...', {
        deviceId: data?.data?.deviceId,
        macAddress: deviceMacAddress
      });
      
   
      let waitCount = 0;
      const maxWait = 20; 
      
      while (getDeviceStatus?.isLoading && waitCount < maxWait) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitCount++;
      }
      
      // Re-check device status after waiting - recalculate filtered status
      const refreshedApiStatus = (getDeviceStatus?.data as any)?.result || [];
      const targetKey = normalizeMacKey(deviceMacAddress);
      const refreshedFiltered = refreshedApiStatus.filter((deviceStatus: any) => {
        const statusMac = deviceStatus?.deviceMac;
        return statusMac && normalizeMacKey(statusMac) === targetKey;
      });
      const refreshedMergedStatus =
        refreshedFiltered?.length > 0 ? refreshedFiltered : wsDeviceStatus || [];
      const refreshedDeviceStatus = refreshedMergedStatus?.[0]?.status;
      const refreshedNormalizedStatus = refreshedDeviceStatus?.toLowerCase()?.trim();
      const refreshedIsDeviceOnline = refreshedNormalizedStatus === "connected" || 
                                      refreshedNormalizedStatus === "running" ||
                                      refreshedNormalizedStatus === "active-running" ||
                                      refreshedNormalizedStatus === "active" ||
                                      refreshedDeviceStatus === "connected" || 
                                      refreshedDeviceStatus === "running";
      
      if (!refreshedDeviceStatus || !refreshedIsDeviceOnline) {
        console.error('Edit Reference Stock: Cannot start workflow - Keonn reader is offline/unreachable (after waiting)', {
          deviceStatus: refreshedDeviceStatus,
          normalizedStatus: refreshedNormalizedStatus,
        deviceId: data?.data?.deviceId,
          deviceName: data?.data?.deviceName,
          deviceMac: deviceMacAddress,
          refreshedMergedStatusLength: refreshedMergedStatus?.length || 0,
          refreshedFilteredLength: refreshedFiltered?.length || 0,
          note: 'Device status is explicitly offline/disconnected - blocking workflow'
        });
        
        Modal.error({
          title: 'Device Connection Error',
          content: 'Please connect to the keonn reader to edit the Reference stock',
          okText: 'OK',
          onOk: () => {
            console.log('Edit Reference Stock: User acknowledged device connection error');
          }
        });
        
        setErsProgressModalVisible(false); // Close progress modal on error
        setErsProgress(0);
        setErsProgressStep('');
        return;
      }
    } else if (isQuerySkipped) {
      // If query was skipped, try to fetch device status one more time
      console.log('Edit Reference Stock: Device status query was skipped - attempting to fetch status...', {
        deviceId: data?.data?.deviceId,
        macAddress: deviceMacAddress
      });
      
      // Refetch device status
      try {
        if (getDeviceStatus?.refetch) {
          await getDeviceStatus.refetch();
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait for data to update
        }
      } catch (refetchError) {
        console.error('Edit Reference Stock: Error refetching device status:', refetchError);
      }
      
      // Re-check device status after refetch - need to get fresh data
      const refreshedApiStatus = (getDeviceStatus?.data as any)?.result || [];
      const targetKey = normalizeMacKey(deviceMacAddress);
      const refreshedFiltered = refreshedApiStatus.filter((deviceStatus: any) => {
        const statusMac = deviceStatus?.deviceMac;
        return statusMac && normalizeMacKey(statusMac) === targetKey;
      });
      const refreshedMergedStatus =
        refreshedFiltered?.length > 0 ? refreshedFiltered : wsDeviceStatus || [];
      const refreshedDeviceStatus = refreshedMergedStatus?.[0]?.status;
      const refreshedNormalizedStatus = refreshedDeviceStatus?.toLowerCase()?.trim();
      const refreshedIsDeviceOffline = refreshedNormalizedStatus === "offline" || 
                                        refreshedNormalizedStatus === "disconnected" ||
                                        refreshedDeviceStatus === "offline" || 
                                        refreshedDeviceStatus === "disconnected";
      
      // Only block if device is explicitly offline/disconnected
      if (refreshedDeviceStatus && refreshedIsDeviceOffline) {
        console.error('Edit Reference Stock: Cannot start workflow - Keonn reader is offline/unreachable (after refetch)', {
          deviceStatus: refreshedDeviceStatus,
          normalizedStatus: refreshedNormalizedStatus,
          deviceId: data?.data?.deviceId,
          deviceName: data?.data?.deviceName,
          deviceMac: deviceMacAddress,
          refreshedMergedStatusLength: refreshedMergedStatus?.length || 0,
          refreshedFilteredLength: refreshedFiltered?.length || 0,
          note: 'Device status is explicitly offline/disconnected - blocking workflow'
        });
        
        Modal.error({
          title: 'Device Connection Error',
          content: 'Please connect to the keonn reader to edit the Reference stock',
          okText: 'OK',
          onOk: () => {
            console.log('Edit Reference Stock: User acknowledged device connection error');
          }
        });
        
        setErsProgressModalVisible(false); // Close progress modal on error
        setErsProgress(0);
        setErsProgressStep('');
        return;
      }
    }

    // Update progress: Validating device connection
    setErsProgress(50);
    setErsProgressStep('Validating device connection...');
    let ersStateId = data?.data?.stateId;
    let ersProcessId = data?.data?.processId;
    let ersDeviceId = data?.data?.deviceId;
    let ersSiteId = data?.data?.siteId;
    let ersZoneId = data?.data?.zoneId;
    const readExecuteStateId = (payload: any) => {
      const root = payload || {};
      const dataNode = root?.data || {};
      const result = root?.result || dataNode?.result || {};
      return root?.stateId || dataNode?.stateId || result?.stateId;
    };
    const readExecuteField = (payload: any, field: string) => {
      const root = payload || {};
      const dataNode = root?.data || {};
      const result = root?.result || dataNode?.result || {};
      return root?.[field] || dataNode?.[field] || result?.[field];
    };

    try {
      // Update progress: Starting workflow
      setErsProgress(60);
      setErsProgressStep('Starting workflow...');
      
      
      const executePayload = {
        userId: user?.userId,
        processId: data?.data?.processId, // Check Reference Stock processId (from activity logs table)
        deviceId: data?.data?.deviceId,
        tenantId: data?.data?.tenantId || tenantId,
        siteId: data?.data?.siteId,
        zoneId: data?.data?.zoneId,
        roleId: user?.roleId,
        isPredefined: true,
        workflowName: "Edit Reference Stock", // Backend maps this to "Check Reference Stock" schema for execution
        workflowNameForStorage: data?.data?.processName || "Edit Reference Stock",
        skipReferenceStockCheck: true, // Allow Edit Reference Stock to start without reference stock
        startedViaEditReferenceStockButton: true // Flag to identify Edit Reference Stock workflow
      };
      
      console.log('[PROCESS START] EDIT REFERENCE STOCK BUTTON - Calling executeProcess API', {
        method: 'EDIT_REFERENCE_STOCK_BUTTON',
        apiEndpoint: 'POST /api/v1/process/execute',
        payload: executePayload,
        flags: {
          startedViaStartButton: false,
          startedViaApplyButton: false,
          startedViaEditReferenceStockButton: true
        },
        processDetails: {
          processId: data?.data?.processId,
          processName: data?.data?.processName || 'Edit Reference Stock',
          deviceId: data?.data?.deviceId,
          siteId: data?.data?.siteId,
          zoneId: data?.data?.zoneId,
          tenantId: data?.data?.tenantId || tenantId,
          userId: user?.userId
        },
        timestamp: new Date().toISOString()
      });
      
      const executeResult = await executeProcess(executePayload).unwrap();
      ersStateId = readExecuteStateId(executeResult) || ersStateId;
      ersProcessId = readExecuteField(executeResult, "processId") || ersProcessId;
      ersDeviceId = readExecuteField(executeResult, "deviceId") || ersDeviceId;
      ersSiteId = readExecuteField(executeResult, "siteId") || ersSiteId;
      ersZoneId = readExecuteField(executeResult, "zoneId") || ersZoneId;
      
      // Update progress: Workflow executed
      setErsProgress(70);
      setErsProgressStep('Workflow executed successfully...');
      
      console.log('[PROCESS START] EDIT REFERENCE STOCK BUTTON - executeProcess API success', {
        method: 'EDIT_REFERENCE_STOCK_BUTTON',
        response: executeResult,
        fullResponse: JSON.stringify(executeResult, null, 2),
        timestamp: new Date().toISOString()
      });
      
      
      setErsProgress(75);
      setErsProgressStep('Saving workflow state...');

      
      console.log('Edit Reference Stock: Waiting 3000ms for state to be committed to database...');
      await new Promise(resolve => setTimeout(resolve, 3000));
      console.log('Edit Reference Stock: Delay completed, proceeding with startProcess...');
      
      // Update progress: Starting process
      setErsProgress(80);
      setErsProgressStep('Starting process...');

   
      if (!ersStateId) {
        message.error({
          content: "stateId is required to start process. Please execute the workflow again.",
          key: "notificationKey",
          duration: 4,
        });
        return;
      }

      const startPayload = {
        userId: user?.userId,
        stateId: ersStateId,
        processId: ersProcessId,
        deviceId: ersDeviceId,
        siteId: ersSiteId,
        zoneId: ersZoneId,
        skipReferenceStockCheck: true, // Allow Edit Reference Stock to start without reference stock
        startedViaEditReferenceStockButton: true // Flag to identify Edit Reference Stock workflow
      };
      
      console.log('[PROCESS START] EDIT REFERENCE STOCK BUTTON - Calling startProcess API', {
        method: 'EDIT_REFERENCE_STOCK_BUTTON',
        apiEndpoint: 'POST /api/v1/process/start',
        payload: startPayload,
        flags: {
          startedViaStartButton: false,
          startedViaApplyButton: false,
          startedViaEditReferenceStockButton: true
        },
        backendNotes: {
          storageProcessId: 'Backend stores state with Set Reference Stock processId',
          executionProcessId: 'We send Check Reference Stock processId',
          searchMethod: 'Backend searches by userId + workflowName (handles deviceId mismatches automatically)'
        },
        timestamp: new Date().toISOString()
      });
      
      const startResult = await startProcess(startPayload).unwrap();
      console.log('[PROCESS START] EDIT REFERENCE STOCK BUTTON - startProcess API success', {
        method: 'EDIT_REFERENCE_STOCK_BUTTON',
        response: startResult,
        timestamp: new Date().toISOString()
      });

      // Update progress: Process started
      setErsProgress(90);
      setErsProgressStep('Process started successfully...');

      // Mark ERS workflow as running
      setErsWorkflowRunning(true);
      
      // Update progress: Opening modal
      setErsProgress(95);
      setErsProgressStep('Opening Edit Reference Stock modal...');
      
      // Open modal after workflow is started
      setEditModalVisible(true);
      setLoadingModalData(true);
      setSelectedRowIds([]);
      
      // Update progress: Complete
      setErsProgress(100);
      setErsProgressStep('Complete!');
      
      // Close progress modal after a brief delay to show completion
      setTimeout(() => {
        setErsProgressModalVisible(false);
        setErsProgress(0);
        setErsProgressStep('');
      }, 300);
      
      // Clear the starting state now that modal is opening
      // The modal's own loading state (loadingModalData) will handle the spinner inside the modal
      setIsStartingERS(false);
      
      // Log WebSocket listener status
      const expectedEventName = `${WebsocketKey.EDIT_REFERENCE_STOCK_DATA}-${data?.data?.deviceId}-${data?.data?.processId}`;
      console.log('[Edit Reference Stock] Workflow started - waiting for WebSocket messages', {
        timestamp: new Date().toISOString(),
        expectedEventName: expectedEventName,
        deviceId: data?.data?.deviceId,
        processId: data?.data?.processId,
        socketConnected: socket?.connected,
        socketId: socket?.id,
        listenerRegistered: !!editRefStockListenerRef.current,
        note: 'Backend should send WebSocket messages to this event name with Check Reference Stock processId (execution processId)'
      });
      
      message.success({ 
        content: "Edit Reference Stock workflow started successfully", 
        key: "notificationKey", 
        className: "successMessage", 
        duration: 2 
      });
    } catch (err: any) {
      const status = err?.status || err?.originalStatus || err?.data?.statusCode;
      const msg = err?.data?.error?.message || err?.data?.message || err?.message || String(err);
      console.error('Edit Reference Stock: start/execute error', { 
        status, 
        msg, 
        errorData: err?.data,
        fullError: err,
        errorType: err?.error?.status,
        requestUrl: err?.meta?.request?.url,
        requestMethod: err?.meta?.request?.method
      });
      
      // Log if request was even attempted
      if (err?.meta) {
        console.error('Edit Reference Stock: Request metadata:', err.meta);
      } else {
        console.error('Edit Reference Stock: No request metadata - request may not have been sent');
      }
      
      // Check if error is about reference stock not being set
      // For Edit Reference Stock, we should allow starting without reference stock
      // This error should only occur for Check Reference Stock, not Edit Reference Stock
      const msgLower = msg?.toLowerCase() || '';
      const isReferenceStockError = msgLower.includes('reference stock not set') ||
                                    msgLower.includes('set the reference stock') ||
                                    msgLower.includes('reference stock not set for this zone');
      
      if (isReferenceStockError) {
        console.error('Edit Reference Stock: Reference stock error detected', {
          errorMessage: msg,
          note: 'Edit Reference Stock should be allowed to start without reference stock. Backend may need to check skipReferenceStockCheck flag or workflowName.'
        });
        // If backend doesn't support skipReferenceStockCheck yet, we'll show the error
        // Otherwise, backend should skip this check for Edit Reference Stock
      }
      
      
      if (status === 404 || msgLower.includes('not running') || msgLower.includes('process not found')) {
        console.log('Edit Reference Stock: Process not running error (404)', {
          status,
          msg,
          note: 'Backend may have found the state but cannot find the running workflow in memory. Checking if process is actually running...'
        });
        
        // Check if the process is actually running (workaround for backend bug)
        const isActuallyRunning = await checkIfProcessIsActive(data?.data?.processId, data?.data?.deviceId);
        
        if (isActuallyRunning) {
          // Process IS running - the start API failed but workflow is active
          // This is a backend bug where it can't find the workflow in memory
          console.log('Edit Reference Stock: Process is actually running despite start API failure - treating as success', {
            processId: data?.data?.processId,
            deviceId: data?.data?.deviceId,
            note: 'Backend bug: workflow is running but start API cannot find it in memory. Proceeding as if start succeeded.'
          });
          
          // Mark as running and open modal
          setErsWorkflowRunning(true);
          setEditModalVisible(true);
          setLoadingModalData(true);
          setSelectedRowIds([]);
          
          message.warning({
            content: "Workflow is running, but the start API reported an error. This is a known backend issue. The workflow should continue normally.",
            key: "ers-workflow-running-despite-error",
            duration: 4
          });
          return; // Exit early - workflow is running
        }
        
        // Process is NOT running - retry startProcess
        console.log('Edit Reference Stock: Process is not running - retrying startProcess...');
        
        // Retry startProcess once after a longer delay (safety net for timing issues)
        // Increased delay to 3000ms to give database more time to commit
        console.log('Edit Reference Stock: Retrying startProcess after longer delay...');
        try {
          await new Promise(resolve => setTimeout(resolve, 3000));
          if (!ersStateId) {
            message.error({
              content: "stateId is required to retry start. Please execute the workflow again.",
              key: "notificationKey",
              duration: 4,
            });
            return;
          }

          const retryStartPayload = {
            userId: user?.userId,
            stateId: ersStateId,
            processId: ersProcessId,
            deviceId: ersDeviceId,
            siteId: ersSiteId,
            zoneId: ersZoneId,
            skipReferenceStockCheck: true // Include flag in retry as well
          };
          const retryResult = await startProcess(retryStartPayload).unwrap();
          console.log('Edit Reference Stock: Retry startProcess API success, response:', retryResult);
          
          // If retry succeeds, continue with normal flow
          setErsWorkflowRunning(true);
          setEditModalVisible(true);
          setLoadingModalData(true);
          setSelectedRowIds([]);
          
          message.success({ 
            content: "Edit Reference Stock workflow started successfully (after retry)", 
            key: "notificationKey", 
            className: "successMessage", 
            duration: 2 
          });
          return; // Exit early on success
        } catch (retryErr: any) {
          const retryStatus = retryErr?.status || retryErr?.originalStatus || retryErr?.data?.statusCode;
          const retryMsg = retryErr?.data?.error?.message || retryErr?.data?.message || retryErr?.message || String(retryErr);
          
          // Check again if process is running after retry
          const isRunningAfterRetry = await checkIfProcessIsActive(data?.data?.processId, data?.data?.deviceId);
          
          if (isRunningAfterRetry) {
            // Process IS running after retry - treat as success
            console.log('Edit Reference Stock: Process is running after retry - treating as success');
            setErsWorkflowRunning(true);
            setEditModalVisible(true);
            setLoadingModalData(true);
            setSelectedRowIds([]);
            
            message.warning({
              content: "Workflow is running, but the start API reported an error. This is a known backend issue. The workflow should continue normally.",
              key: "ers-workflow-running-after-retry",
              duration: 4
            });
            return;
          }
          
          console.error('Edit Reference Stock: Retry startProcess also failed and process is not running', {
            status: retryStatus,
            msg: retryMsg,
            errorData: retryErr?.data,
            fullError: retryErr,
            note: 'Backend found the state but cannot find the running workflow in memory. This may be a backend issue where the workflow lookup by processId fails. The backend should look up the workflow by workflowId (stored in the state) or by deviceId instead.'
          });
          message.error({
            content: "Failed to start workflow. The backend found the process state but cannot find the running workflow. This may be a timing issue or a backend bug. Please try again or contact support.",
            key: "ers-process-not-running",
            duration: 6
          });
          setErsWorkflowRunning(false);
        }
      }
      else {
        message.error(msg || "Failed to start Edit Reference Stock workflow");
      }
    } finally {
      setErsProgressModalVisible(false);
      setErsProgress(0);
      setErsProgressStep('');
      setIsStartingERS(false);
    }
  };

  // Stop Edit Reference Stock workflow
  const handleStopERSWorkflow = async () => {
    if (!data?.data?.processId || !data?.data?.deviceId) {
      // If required data is missing, just close the modal
      setEditModalVisible(false);
      setSelectedRowIds([]);
      setActivePanel('scanned');
      
      // Clear manually moved/removed tracking when modal closes
      manuallyMovedToCurrentStockRef.current.clear();
      manuallyRemovedFromCurrentStockRef.current.clear();
      console.log('[Edit Reference Stock] Cleared manually moved/removed tracking on modal close');
      
      // Refresh activity logs when modal closes
      console.log('[Edit Reference Stock] Modal closed - refreshing activity logs...');
      try {
        await tagsProcessData.refetch();
        console.log('[Edit Reference Stock] Activity logs refreshed successfully');
      } catch (refetchError) {
        console.error('[Edit Reference Stock] Error refreshing activity logs:', refetchError);
      }
      return;
    }

    // Always call the stop API when Exit button is pressed
    try {
  
      let processIdToStop = data?.data?.processId;
      const currentSiteId = data?.data?.siteId || '';
      const currentZoneId = data?.data?.zoneId || '';
      const currentDeviceId = data?.data?.deviceId;
      
      
        const activeProcesses = activeProcessesData?.result || [];
        
      // First, try to find the process with exact processId match
      let matchingProcess = activeProcesses.find((p: any) => {
        const siteIdMatches = (p.siteId || '') === currentSiteId;
        const zoneIdMatches = (p.zoneId || '') === currentZoneId;
        const deviceIdMatches = p.deviceId === currentDeviceId;
        const processIdMatches = p.processId === processIdToStop;
        // CRITICAL: Require siteId, zoneId, deviceId, AND processId to match
        return siteIdMatches && zoneIdMatches && deviceIdMatches && processIdMatches;
      });
      
      // If not found, try to find Set Reference Stock processId (backend might use different processId)
      // But STILL filter by site/zone/device to prevent stopping processes in other sites/zones
      if (!matchingProcess) {
        matchingProcess = activeProcesses.find((p: any) => {
          const siteIdMatches = (p.siteId || '') === currentSiteId;
          const zoneIdMatches = (p.zoneId || '') === currentZoneId;
          const deviceIdMatches = p.deviceId === currentDeviceId;
          const processNameMatches = p.processName === "Set Reference Stock" || 
                                     p.workflowName === "Set Reference Stock" ||
                                     p.processName === "Edit Reference Stock" ||
                                     p.workflowName === "Edit Reference Stock";
          // CRITICAL: Require siteId, zoneId, deviceId, AND processName to match
          return siteIdMatches && zoneIdMatches && deviceIdMatches && processNameMatches;
        });
        
        if (matchingProcess?.processId) {
          processIdToStop = matchingProcess.processId;
          console.log('[Edit Reference Stock] Found Set Reference Stock processId for current site/zone/device:', {
            foundProcessId: matchingProcess.processId,
            originalProcessId: data?.data?.processId,
            siteId: matchingProcess.siteId,
            zoneId: matchingProcess.zoneId,
            deviceId: matchingProcess.deviceId
          });
        }
      }
      
      if (!matchingProcess) {
        console.warn('[Edit Reference Stock] Process not found in active processes for current context - may have already stopped:', {
          processId: processIdToStop,
          siteId: currentSiteId,
          zoneId: currentZoneId,
          deviceId: currentDeviceId,
          totalActiveProcesses: activeProcesses.length,
          note: 'Will still attempt to stop using processId to ensure cleanup'
        });
      } else {
        console.log('[Edit Reference Stock] Verified active process matches current context before stopping:', {
          processId: matchingProcess.processId,
          siteId: matchingProcess.siteId,
          zoneId: matchingProcess.zoneId,
          deviceId: matchingProcess.deviceId,
          processName: matchingProcess.processName,
          workflowName: matchingProcess.workflowName
        });
      }
      
      console.log('[Edit Reference Stock] Stopping process with processId:', {
        processId: processIdToStop,
        siteId: data?.data?.siteId,
        zoneId: data?.data?.zoneId,
        deviceId: data?.data?.deviceId,
        note: 'Stopping only the process for this specific site/zone/device combination'
      });
      await stopProcess({
        stopBy: user?.userId,
        processId: processIdToStop, // Use the found processId or fallback to original
        processName: "Edit Reference Stock",
        deviceId: data?.data?.deviceId,
        siteId: data?.data?.siteId, // Include siteId to ensure backend stops only this site's process
        zoneId: data?.data?.zoneId, // Include zoneId to ensure backend stops only this zone's process
        type: "exit",
        activity_logs: true,
        userId: user?.userId,
      }).unwrap();

      console.log('Edit Reference Stock workflow stopped successfully');
      

      setErsWorkflowRunning(false);
      setEditModalVisible(false);
      setSelectedRowIds([]);
      setActivePanel('scanned');
      
      manuallyMovedToCurrentStockRef.current.clear();
      manuallyRemovedFromCurrentStockRef.current.clear();
      console.log('[Edit Reference Stock] Cleared manually moved/removed tracking on modal close');
      console.log('[Edit Reference Stock] Preserved currentStock and scannedTags data - not clearing on workflow stop', {
        currentStockCount: currentStock.length,
        scannedTagsCount: scannedTags.length
      });
      
      // Refresh activity logs when modal closes
      console.log('[Edit Reference Stock] Modal closed - refreshing activity logs...');
      try {
        await tagsProcessData.refetch();
        console.log('[Edit Reference Stock] Activity logs refreshed successfully');
      } catch (refetchError) {
        console.error('[Edit Reference Stock] Error refreshing activity logs:', refetchError);
      }
      
      // Show success toast message
      message.success({ 
        content: "Edit Reference Stock process stopped successfully", 
        key: "notificationKey", 
        className: "successMessage", 
        duration: 2 
      });
    } catch (err: any) {
      const msg = err?.data?.error?.message || err?.data?.message || err?.message || String(err);
      console.error('Edit Reference Stock: stop error', { msg, errorData: err?.data });
      
      // Still close the modal even if stop fails
      setErsWorkflowRunning(false);
      setEditModalVisible(false);
      setSelectedRowIds([]);
      setActivePanel('scanned');
      
      // Clear manually moved/removed tracking when modal closes
      manuallyMovedToCurrentStockRef.current.clear();
      manuallyRemovedFromCurrentStockRef.current.clear();
      console.log('[Edit Reference Stock] Cleared manually moved/removed tracking on modal close');
      
      // Refresh activity logs when modal closes to reflect any reference stock changes (add/delete operations)
      // Backend fix prevents Edit Reference Stock workflow scans from affecting Check Reference Stock scan counts
      console.log('[Edit Reference Stock] Modal closed (after stop error) - refreshing activity logs to reflect reference stock changes...');
      try {
        await tagsProcessData.refetch();
        console.log('[Edit Reference Stock] Activity logs refreshed successfully');
      } catch (refetchError) {
        console.error('[Edit Reference Stock] Error refreshing activity logs:', refetchError);
      }
      
      message.warning(msg || "Failed to stop workflow, but modal closed");
    }
  };

  // Handle modal open - show loading initially only if we don't have data
  useEffect(() => {
    if (editModalVisible) {
      setSelectedRowIds([]);
      // Only show loading if we don't have data for the active panel
      // If data exists, it means the workflow was stopped and restarted - preserve the data
      if (activePanel === 'current' && currentStock.length === 0) {
        setLoadingModalData(true);
      } else if (activePanel === 'scanned' && scannedTags.length === 0) {
        setLoadingModalData(true);
      } else {
        // We have existing data - don't show loading
        setLoadingModalData(false);
      }
    }
  }, [editModalVisible, activePanel, currentStock.length, scannedTags.length]);

  // Clear loading state when data arrives for the active panel
  useEffect(() => {
    if (editModalVisible && loadingModalData) {
      // Clear loading when data is available for the active panel
      if (activePanel === 'current' && currentStock.length > 0) {
        setLoadingModalData(false);
      } else if (activePanel === 'scanned' && scannedTags.length > 0) {
        setLoadingModalData(false);
      }
    }
  }, [editModalVisible, loadingModalData, activePanel, currentStock.length, scannedTags.length]);

  // Early return if data is not available (after all hooks)
  if (!data || !data?.data) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>No process data available. Please select a process from the activity logs.</p>
      </div>
    );
  }

  // Helper function to check if error is related to external solution API failure
  const isExternalSolutionError = (error: any): boolean => {
    const errorMsg = (error?.data?.error?.message || error?.data?.message || error?.message || '').toLowerCase();
    return errorMsg.includes('external solution') || 
           errorMsg.includes('local database was not updated') ||
           errorMsg.includes('failed to sync');
  };

  // Helper function to extract user-friendly error message
  // Always display the actual backend error message (including external solution errors)
  const getErrorMessage = (error: any, operation: 'add' | 'delete'): string => {
    // Extract the actual backend error message from various possible locations
    const backendErrorMsg = error?.data?.error?.message || 
                           error?.data?.message || 
                           error?.data?.error || 
                           error?.message;
    
    // Check for portal connection errors and replace with digitization error
    if (backendErrorMsg && (
      backendErrorMsg.toLowerCase().includes('unable to connect to portal') ||
      backendErrorMsg.toLowerCase().includes('unable to connect to inventory portal') ||
      backendErrorMsg.toLowerCase().includes('connect to portal right now') ||
      backendErrorMsg.toLowerCase().includes('try again later')
    )) {
      return "Tag is not digitised. Please digitise the tag and try again later";
    }
    
    // Specific error message for tag not digitized
    if (backendErrorMsg?.toLowerCase().includes('tag is not digitized') || 
        backendErrorMsg?.toLowerCase().includes('tag is not digitised')) {
      return "Tag is not digitised. Please digitise the tag and try again later";
    }
    
    // Always show the backend error message if available
    if (backendErrorMsg) {
      return backendErrorMsg;
    }
    
    // Fallback if no error message is available
    return `Failed to ${operation} tags`;
  };

  const isTagNotDigitisedMessage = (msg: string) => {
    const lower = msg?.toLowerCase?.() || '';
    return lower.includes('tag is not digitised') || lower.includes('tag is not digitized');
  };

  // Handle add tags confirmation - only one tag at a time
  const handleAddConfirm = async () => {
    setAddConfirmVisible(false);
    
    if (selectedRowIds.length === 0) {
      message.warning('Please select a tag to add');
      return;
    }

    // Get selected scanned tag (only one)
    const selectedTag = scannedTags.find(tag => selectedRowIds.includes(tag.id));
    
    if (!selectedTag) {
      message.warning('Selected tag not found');
      return;
    }

    let normalizedEpc = '';
    let previousScannedTagsSnapshot: any[] = [];
    let previousCurrentStockSnapshot: any[] = [];
    let previousMovedTimestamp: number | undefined;
    let previousRemovedTimestamp: number | undefined;

    try {
      const tagToAdd = {
        epc: selectedTag.epc,
        tagId: selectedTag.tagId || undefined,
        upc: selectedTag.upc || undefined,
        productDescription: selectedTag.description || selectedTag.productDescription || undefined
      };

      // Set loading state for tag action
      setIsTagActionLoading(true);

      // OPTIMISTIC UI UPDATE (no lag)
      cleanupPendingTagActions();
      normalizedEpc = selectedTag.epc;
      previousScannedTagsSnapshot = scannedTags;
      previousCurrentStockSnapshot = currentStock;
      previousMovedTimestamp = manuallyMovedToCurrentStockRef.current.get(normalizedEpc);
      previousRemovedTimestamp = manuallyRemovedFromCurrentStockRef.current.get(normalizedEpc);

      console.log('[Add Tags] Optimistic update - removing from scannedTags and adding to currentStock', {
        epc: selectedTag.epc,
        normalizedEpc: normalizedEpc,
        tagId: selectedTag.id
      });

      // Track this EPC as manually moved to currentStock FIRST (before state updates)
      manuallyMovedToCurrentStockRef.current.set(normalizedEpc, Date.now());
      pendingTagActionsRef.current.set(normalizedEpc, { action: 'add', timestamp: Date.now() });

      // Remove EPC from manually removed tracking (if it was previously removed)
      if (normalizedEpc && manuallyRemovedFromCurrentStockRef.current.has(normalizedEpc)) {
        manuallyRemovedFromCurrentStockRef.current.delete(normalizedEpc);
      }

      // Remove from scannedTags - filter by both id and EPC to ensure removal
      setScannedTags(prevScannedTags => prevScannedTags.filter(tag => {
        const tagEpc = tag.epc.trim();
        const matchesId = tag.id !== selectedTag.id;
        const matchesEpc = tagEpc !== normalizedEpc;
        return matchesId && matchesEpc;
      }));

      // Add to currentStock with proper format
      const tagToAddToCurrentStock = {
        ...selectedTag,
        epc: normalizedEpc, // Use normalized EPC
        id: `current-${normalizedEpc}-${selectedTag.tagId || Date.now()}`,
        key: `current-${normalizedEpc}-${selectedTag.tagId || Date.now()}`,
        description: selectedTag.description || selectedTag.productDescription || '',
        isVerified: true // Newly added tags are verified
      };

      setCurrentStock(prevCurrentStock => {
        const normalizedEpcs = prevCurrentStock.map(tag => tag.epc.trim());
        if (normalizedEpcs.includes(normalizedEpc)) {
          return prevCurrentStock;
        }
        return [...prevCurrentStock, tagToAddToCurrentStock];
      });

      // Clear selection
      setSelectedRowIds([]);

      // Ignore WebSocket updates briefly to prevent overwrites
      ignoreWebSocketUpdatesRef.current = true;
      lastManualUpdateTimeRef.current = Date.now();

      // NOTE: Do not perform add-style optimistic updates in delete flow.

      // Find the correct processId for add operation
      // Backend stores state with Set Reference Stock processId, but we might have Check Reference Stock processId
      let processIdToUse = data?.data?.processId; // Default to Check Reference Stock processId
      
      try {
        // Refetch active processes to get the latest data
        await refetchActiveProcesses();
        // Wait a bit for the query to update
        await new Promise(resolve => setTimeout(resolve, 500));
        // Use the cached data from the query hook
        const activeProcesses = activeProcessesData?.result || [];
        
        // Backend stores state with Set Reference Stock processId for Edit Reference Stock workflow
        const currentSiteId = data?.data?.siteId || '';
        const currentZoneId = data?.data?.zoneId || '';
        const currentDeviceId = data?.data?.deviceId;
        
        const activeProcess = activeProcesses.find((p: any) => {
          const siteIdMatches = (p.siteId || '') === currentSiteId;
          const zoneIdMatches = (p.zoneId || '') === currentZoneId;
          const deviceIdMatches = p.deviceId === currentDeviceId;
          const processNameMatches = p.processName === "Check Reference Stock" || 
                                     p.processName === "Edit Reference Stock" ||
                                     p.processName === "Set Reference Stock" ||
                                     p.workflowName === "Check Reference Stock" ||
                                     p.workflowName === "Edit Reference Stock" ||
                                     p.workflowName === "Set Reference Stock";
          // CRITICAL: Require siteId, zoneId, deviceId, AND processName to match
          return siteIdMatches && zoneIdMatches && deviceIdMatches && processNameMatches;
        });
        
        if (activeProcess?.processId) {
          processIdToUse = activeProcess.processId;
          console.log('[Add Tags] Found active process for add operation:', {
            foundProcessId: activeProcess.processId,
            originalProcessId: data?.data?.processId,
            siteId: activeProcess.siteId,
            zoneId: activeProcess.zoneId,
            deviceId: activeProcess.deviceId,
            processName: activeProcess.processName,
            workflowName: activeProcess.workflowName
          });
        } else {
          console.log('[Add Tags] Active process not found, using original processId:', processIdToUse);
        }
      } catch (findError) {
        console.error('[Add Tags] Error finding active process, using original processId:', findError);
        // Continue with original processId if finding active process fails
      }

      console.log('[Add Tags] Using processId for add operation:', processIdToUse);
      await addTagsToReferenceStock({
        processId: processIdToUse, // Use the found processId or fallback to original
        siteId: data?.data?.siteId,
        zoneId: data?.data?.zoneId,
        userId: user?.userId,
        tags: [tagToAdd] // API expects array, but we only send one tag
      }).unwrap();

      message.success(`Successfully added tag to reference stock`);
      
      // Set flag to ignore WebSocket updates for 2 seconds to prevent conflicts
      setTimeout(() => {
        ignoreWebSocketUpdatesRef.current = false;
        console.log('[Add Tags] Re-enabled WebSocket updates after optimistic update');
      }, 2000);
      
      // Clear loading state
      setIsTagActionLoading(false);
      
      console.log('[Add Tags] UI updated immediately, backend refresh triggered');
    } catch (error: any) {
      // Rollback optimistic update
      setScannedTags(previousScannedTagsSnapshot);
      setCurrentStock(previousCurrentStockSnapshot);

      if (previousMovedTimestamp === undefined) {
        manuallyMovedToCurrentStockRef.current.delete(normalizedEpc);
      } else {
        manuallyMovedToCurrentStockRef.current.set(normalizedEpc, previousMovedTimestamp);
      }

      if (previousRemovedTimestamp === undefined) {
        manuallyRemovedFromCurrentStockRef.current.delete(normalizedEpc);
      } else {
        manuallyRemovedFromCurrentStockRef.current.set(normalizedEpc, previousRemovedTimestamp);
      }

      ignoreWebSocketUpdatesRef.current = false;
      setIsTagActionLoading(false);
      const errorMsg = getErrorMessage(error, 'add');
      if (isTagNotDigitisedMessage(errorMsg)) {
        message.open({
          type: 'error',
          content: errorMsg,
          duration: 5,
          className: 'edit-reference-stock-error-toast'
        });
      } else {
      message.error(errorMsg, 5); // Show error for 5 seconds
      }
      console.error('[Add Tags to Reference Stock] Error:', {
        error,
        isExternalSolutionError: isExternalSolutionError(error),
        errorMessage: error?.data?.error?.message || error?.data?.message || error?.message
      });
    } finally {
      // Always refresh inventory data after add attempt (background update for Activity Logs table)
      console.log('[Add Tags] Refreshing inventory data from backend after add attempt');
      try {
        await tagsProcessData.refetch();
        console.log('[Add Tags] Inventory data refreshed successfully');
      } catch (refetchError) {
        console.error('[Add Tags] Error refreshing inventory data:', refetchError);
      }
    }
  };

  // Handle delete tags confirmation - only one tag at a time
  const handleDeleteConfirm = async () => {
    setDeleteConfirmVisible(false);
    
    if (selectedRowIds.length === 0) {
      message.warning('Please select a tag to delete');
      return;
    }

    // Get selected current stock tag (only one)
    const selectedTag = currentStock.find(tag => selectedRowIds.includes(tag.id));
    
    if (!selectedTag) {
      message.warning('Selected tag not found');
      return;
    }

    let normalizedEpc = '';
    let previousScannedTagsSnapshot: any[] = [];
    let previousCurrentStockSnapshot: any[] = [];
    let previousMovedNormalizedTimestamp: number | undefined;
    let previousMovedRawTimestamp: number | undefined;
    let previousRemovedTimestamp: number | undefined;

    try {
      const tagToDelete = {
        epc: selectedTag.epc,
        tagId: selectedTag.tagId || undefined
      };

      // Set loading state for tag action
      setIsTagActionLoading(true);

      // OPTIMISTIC UI UPDATE (no lag)
      cleanupPendingTagActions();
      normalizedEpc = selectedTag.epc.trim();
      previousScannedTagsSnapshot = scannedTags;
      previousCurrentStockSnapshot = currentStock;
      previousMovedNormalizedTimestamp = manuallyMovedToCurrentStockRef.current.get(normalizedEpc);
      previousMovedRawTimestamp = manuallyMovedToCurrentStockRef.current.get(selectedTag.epc.trim());
      previousRemovedTimestamp = manuallyRemovedFromCurrentStockRef.current.get(normalizedEpc);

      console.log('[Delete Tags] Optimistic update - removing from currentStock', {
        epc: selectedTag.epc,
        normalizedEpc: normalizedEpc,
        tagId: selectedTag.id
      });

      // Remove EPC from manually moved tracking (if it was manually moved)
      if (normalizedEpc) {
        manuallyMovedToCurrentStockRef.current.delete(normalizedEpc);
        manuallyMovedToCurrentStockRef.current.delete(selectedTag.epc);
      }

      // Track this EPC as manually removed from currentStock (use normalized EPC)
      if (normalizedEpc) {
        manuallyRemovedFromCurrentStockRef.current.set(normalizedEpc, Date.now());
        pendingTagActionsRef.current.set(normalizedEpc, { action: 'delete', timestamp: Date.now() });
      }

      // Remove from currentStock - filter by both id and EPC to ensure removal
      setCurrentStock(prevCurrentStock => prevCurrentStock.filter(tag => {
        const tagEpc = tag.epc.trim();
        const matchesId = tag.id !== selectedTag.id;
        const matchesEpc = tagEpc !== normalizedEpc;
        return matchesId && matchesEpc;
      }));

      // If the tag is being scanned, add it to scannedTags (if not already there)
      setScannedTags(prevScannedTags => {
        const normalizedEpcs = prevScannedTags.map(tag => tag.epc.trim());
        if (normalizedEpcs.includes(normalizedEpc)) {
          return prevScannedTags;
        }
        if (normalizedEpc) {
          const tagToAddToScanned = {
            ...selectedTag,
            epc: normalizedEpc,
            tagId: selectedTag.tagId || undefined,
            upc: selectedTag.upc || '',
            description: selectedTag.description || selectedTag.productDescription || '',
            productDescription: selectedTag.description || selectedTag.productDescription || '',
            rssi: '',
            port: '',
            id: `scanned-${normalizedEpc}-${selectedTag.tagId || Date.now()}`,
            key: `scanned-${normalizedEpc}-${selectedTag.tagId || Date.now()}`
          };
          return [...prevScannedTags, tagToAddToScanned];
        }
        return prevScannedTags;
      });

      // Clear selection
      setSelectedRowIds([]);

      // Ignore WebSocket updates briefly to prevent overwrites
      ignoreWebSocketUpdatesRef.current = true;
      lastManualUpdateTimeRef.current = Date.now();

      // Find the correct processId for delete operation
      // Backend stores state with Set Reference Stock processId, but we might have Check Reference Stock processId
      let processIdToUse = data?.data?.processId; // Default to Check Reference Stock processId
      
      try {
        // Refetch active processes to get the latest data
        await refetchActiveProcesses();
        // Wait a bit for the query to update
        await new Promise(resolve => setTimeout(resolve, 500));
        // Use the cached data from the query hook
        const activeProcesses = activeProcessesData?.result || [];
        
        // Find the active Edit Reference Stock / Set Reference Stock process for this device
        // CRITICAL: Filter by siteId, zoneId, deviceId, AND processName to prevent affecting processes in other sites/zones
        // Backend stores state with Set Reference Stock processId for Edit Reference Stock workflow
        const currentSiteId = data?.data?.siteId || '';
        const currentZoneId = data?.data?.zoneId || '';
        const currentDeviceId = data?.data?.deviceId;
        
        const activeProcess = activeProcesses.find((p: any) => {
          const siteIdMatches = (p.siteId || '') === currentSiteId;
          const zoneIdMatches = (p.zoneId || '') === currentZoneId;
          const deviceIdMatches = p.deviceId === currentDeviceId;
          const processNameMatches = p.processName === "Check Reference Stock" || 
                                     p.processName === "Edit Reference Stock" ||
                                     p.processName === "Set Reference Stock" ||
                                     p.workflowName === "Check Reference Stock" ||
                                     p.workflowName === "Edit Reference Stock" ||
                                     p.workflowName === "Set Reference Stock";
          // CRITICAL: Require siteId, zoneId, deviceId, AND processName to match
          return siteIdMatches && zoneIdMatches && deviceIdMatches && processNameMatches;
        });
        
        if (activeProcess?.processId) {
          processIdToUse = activeProcess.processId;
          console.log('[Delete Tags] Found active process for delete operation:', {
            foundProcessId: activeProcess.processId,
            originalProcessId: data?.data?.processId,
            siteId: activeProcess.siteId,
            zoneId: activeProcess.zoneId,
            deviceId: activeProcess.deviceId,
            processName: activeProcess.processName,
            workflowName: activeProcess.workflowName
          });
        } else {
          console.log('[Delete Tags] Active process not found, using original processId:', processIdToUse);
        }
      } catch (findError) {
        console.error('[Delete Tags] Error finding active process, using original processId:', findError);
        // Continue with original processId if finding active process fails
      }

      console.log('[Delete Tags] Using processId for delete operation:', processIdToUse);
      await deleteTagsFromReferenceStock({
        processId: processIdToUse, // Use the found processId or fallback to original
        siteId: data?.data?.siteId,
        zoneId: data?.data?.zoneId,
        userId: user?.userId,
        tags: [tagToDelete] // API expects array, but we only send one tag
      }).unwrap();

      message.success(`Successfully deleted tag from reference stock`);
      
      // Set flag to ignore WebSocket updates for 2 seconds to prevent conflicts
      setTimeout(() => {
        ignoreWebSocketUpdatesRef.current = false;
      }, 2000);
      
      // Clear loading state
      setIsTagActionLoading(false);
      
      
      console.log('[Delete Tags] Waiting for WebSocket update to refresh current stock and scanned tags in modal');
    } catch (error: any) {
      
      setScannedTags(previousScannedTagsSnapshot);
      setCurrentStock(previousCurrentStockSnapshot);

      if (previousMovedNormalizedTimestamp === undefined) {
        manuallyMovedToCurrentStockRef.current.delete(normalizedEpc);
      } else {
        manuallyMovedToCurrentStockRef.current.set(normalizedEpc, previousMovedNormalizedTimestamp);
      }

      if (previousMovedRawTimestamp === undefined) {
        manuallyMovedToCurrentStockRef.current.delete(selectedTag.epc);
      } else {
        manuallyMovedToCurrentStockRef.current.set(selectedTag.epc, previousMovedRawTimestamp);
      }

      if (previousRemovedTimestamp === undefined) {
        manuallyRemovedFromCurrentStockRef.current.delete(normalizedEpc);
      } else {
        manuallyRemovedFromCurrentStockRef.current.set(normalizedEpc, previousRemovedTimestamp);
      }

      ignoreWebSocketUpdatesRef.current = false;
      setIsTagActionLoading(false);
      const errorMsg = getErrorMessage(error, 'delete');
      if (isTagNotDigitisedMessage(errorMsg)) {
        message.open({
          type: 'error',
          content: errorMsg,
          duration: 5,
          className: 'edit-reference-stock-error-toast'
        });
      } else {
      message.error(errorMsg, 5); 
      }
      console.error('[Delete Tags from Reference Stock] Error:', {
        error,
        isExternalSolutionError: isExternalSolutionError(error),
        errorMessage: error?.data?.error?.message || error?.data?.message || error?.message
      });
    } finally {
      // Always refresh inventory data after delete attempt (background update for Activity Logs table)
      console.log('[Delete Tags] Refreshing inventory data from backend after delete attempt');
      try {
        await tagsProcessData.refetch();
        console.log('[Delete Tags] Inventory data refreshed successfully');
      } catch (refetchError) {
        console.error('[Delete Tags] Error refreshing inventory data:', refetchError);
      }
    }
  };

  // Handle row selection - only allow single selection
  const handleRowSelection = {
    type: 'radio' as const, // Single selection only
    selectedRowKeys: selectedRowIds.length > 0 ? [selectedRowIds[0]] : [],
    onChange: (selectedKeys: React.Key[]) => {
      // Only allow one selection at a time
      setSelectedRowIds(selectedKeys.length > 0 ? [selectedKeys[0] as string] : []);
    },
    getCheckboxProps: (record: any) => ({
      name: record.id,
    }),
  };

  return (
    <>
      {showAlert && (
        <div className="alert-overlay">
          <Alert
            description={
              notifyAlert?.error?.message
                ? notifyAlert?.error?.message
                : notifyAlert?.message
            }
            type="info"
            className="alertColor"
            showIcon
            closable
            style={{ marginTop: 16 }}
            onClose={handleClose}
          />
        </div>
      )}
      <Spin spinning={!!loadingBar || isStartingERS} tip={isStartingERS ? "Starting Edit Reference Stock..." : "Loading..."} wrapperClassName="spin-class" style={{justifyContent:"center", alignItems: "center", display:"flex"}}>
        <div className="al-wrapper">
       
          <Row className="al-header-row1">
            <Col span={24}>
                  <div onClick={() => navigate(Page.ACTIVE_PROCESS)} className="backButton">
                    <LeftOutlined className="bckIcn left-back-button" />
                    <b className="top-back-text">BACK</b>
                  </div>
                </Col>
          </Row>

          <Row className="al-header-row2" align="middle" gutter={24}>
            <Col xs={24} md={17} className="al-title-left">
              <div className="al-title-actions">
                <div className="al-title">Reference Stock</div>
                <div className="al-actions-spacer"></div>
                <div className="al-actions">
                  <button className="ant-btn ant-btn-default hover-elevate al-btn-consistent" onClick={refresh}>
                    <SyncOutlined className="reloadButton" /> Refresh
                  </button>
                    <button 
                      type="button" 
                    className="ant-btn primary-btn al-btn-consistent"
                      style={{ 
                        backgroundColor: crsWorkflowRunning ? '#d9d9d9' : '#227f99', 
                        borderColor: crsWorkflowRunning ? '#d9d9d9' : '#227f99', 
                        color: '#fff',
                        cursor: crsWorkflowRunning ? 'not-allowed' : 'pointer'
                      }}
                      onClick={handleEditReferenceStockClick}
                      disabled={crsWorkflowRunning || ersWorkflowRunning || isStartingERS || executeInfo?.isLoading || startInfo?.isLoading}
                    title={crsWorkflowRunning ? 'Check Reference Stock is running. Please stop it first.' : 'Edit Reference Stock'}
                    >
                      {isStartingERS || executeInfo?.isLoading || startInfo?.isLoading ? 'Starting...' : 'Edit Reference Stock'}
                    </button>
                </div>
                  </div>
                </Col>
            {/* Right: Check Reference Stock heading - aligned above right card (desktop only) */}
            <Col xs={0} md={7} className="al-title-right">
              <div className="al-title">Check Reference Stock</div>
            </Col>
              </Row>

          {/* Main content: equal height cards */}
          <Row gutter={24} className="al-main" align="stretch">
            {/* Left card */}
            <Col xs={24} md={17} className="al-col-stretch">
              <Card className="uicard table activityLogsTable al-card-stretch" bodyStyle={{height:'100%'}}>
              <ActivityLogsTable
                logsTableData={data}
                tagsReadData={tagsProcessData}
                processData={data?.data}
                setLoadingBar={setLoadingBar}
              />
            </Card>
          </Col>
          
            {/* Right card */}
            <Col xs={24} md={7} className="al-col-stretch">
              {/* Check Reference Stock heading above right card (mobile only - desktop shows in header row) */}
              <div className="al-right-header-mobile">
                <div className="al-title">Check Reference Stock</div>
              </div>
              <Card className="uicard activityLogsTable al-card-stretch" bodyStyle={{height:'100%'}}>
              {data && (
              <DeviceDetail
                data={data}
                syncedData={syncedData}
                setSyncedData={setSyncedData}
                loadingBar={loadingBar}
                setLoadingBar={setLoadingBar}
                  onProcessStateChange={handleCrsProcessStateChange}
              />
              )}
            </Card>
          </Col>
        </Row>
        </div>
        </Spin>
        
      {/* Progress Modal for Edit Reference Stock */}
        <Modal
          title="Opening Edit Reference Stock"
          visible={ersProgressModalVisible}
          footer={null}
          closable={false}
          maskClosable={false}
          width={500}
          centered
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Progress 
              percent={ersProgress} 
              status={ersProgress === 100 ? 'success' : 'active'}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
              {ersProgressStep || 'Please wait...'}
            </div>
          </div>
        </Modal>
        
      {/* Edit Reference Stock Modal - show only active panel */}
        <Modal
          title="Edit Reference Stock"
          visible={editModalVisible}
          onCancel={handleStopERSWorkflow}
          footer={null}
          closable={false}
        width={"90vw"}
        bodyStyle={{ height: '80vh', overflowY: 'auto', overflowX: 'auto', maxHeight: '80vh' }}
          className="edit-reference-modal"
        >
        {/* Toggle headers */}
        <Row gutter={16} style={{ marginBottom: '12px' }}>
            <Col span={24}>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                type={activePanel === 'current' ? 'primary' : 'default'}
                style={activePanel === 'current' ? { backgroundColor: '#227f99', borderColor: '#227f99' } : {}}
                onClick={() => { 
                  setActivePanel('current'); 
                  setSelectedRowIds([]);
                  setCurrentStockPagination({ current: 1, pageSize: 10 });
                }}
              >
                  Current Reference Stock
              </Button>
                <Button
                  type={activePanel === 'scanned' ? 'primary' : 'default'}
                style={activePanel === 'scanned' ? { backgroundColor: '#227f99', borderColor: '#227f99' } : {}}
                onClick={() => { 
                  setActivePanel('scanned'); 
                  setSelectedRowIds([]);
                  setScannedTagsPagination({ current: 1, pageSize: 10 });
                }}
                >
                  New Scanned Live Tags
                </Button>
              </div>
              <Button
                onClick={handleStopERSWorkflow}
                className="edit-reference-modal-exit-button"
              >
                EXIT
                </Button>
              </div>
            </Col>
          </Row>

        {/* Active panel full width */}
        {activePanel === 'current' ? (
            <>
              <Spin spinning={loadingModalData || isTagActionLoading} tip={isTagActionLoading ? "Processing tag operation..." : "Loading current stock..."}>
              <div className="edit-reference-stock-table-wrapper">
              <Table
                  rowSelection={handleRowSelection}
                  className="edit-reference-stock-table"
                columns={[
                    { 
                      title: 'Product Description', 
                      dataIndex: 'description', 
                      key: 'description', 
                      align: 'left',
                      ellipsis: {
                        showTitle: false,
                      },
                      render: (text: string, record: any) => {
                        const fullDescription = text || record?.productDescription || '-';
                        return (
                          <UITooltip 
                            title={fullDescription} 
                            placement="top"
                            overlayStyle={{ maxWidth: '800px', wordWrap: 'break-word', whiteSpace: 'normal' }}
                            getPopupContainer={(trigger: HTMLElement) => trigger.parentElement || document.body}
                          >
                            <div style={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              maxWidth: '100%'
                            }}>
                              {fullDescription}
                            </div>
                          </UITooltip>
                        );
                      }
                    },
                    { title: 'EPC', dataIndex: 'epc', key: 'epc', align: 'left' },
                    { title: 'TagID', dataIndex: 'tagId', key: 'tagId', align: 'left', render: (text: string) => text || '-' },
                    { title: 'UPC', dataIndex: 'upc', key: 'upc', align: 'left', render: (text: string) => text || '-' },
                    { 
                      title: 'Status', 
                      dataIndex: 'isVerified', 
                      key: 'isVerified',
                      align: 'left',
                      render: (isVerified: boolean) => (
                        <span style={{ color: isVerified ? '#52c41a' : '#ff4d4f' }}>
                           {isVerified ? 'Present' : 'Missing'}
                        </span>
                      )
                    }
                  ]}
                  dataSource={currentStock.slice(
                    (currentStockPagination.current - 1) * currentStockPagination.pageSize,
                    currentStockPagination.current * currentStockPagination.pageSize
                  )}
                  rowKey={(record: any, index?: number) => {
                    const uniqueId = record?.id || record?.key || `current-${record?.epc || ''}-${index ?? 0}`;
                    return uniqueId || `current-row-${index ?? 0}`;
                  }}
                pagination={false}
              scroll={{ x: 'max-content', y: 500 }}
                  locale={{
                    emptyText: (loadingModalData || isTagActionLoading) ? 'Loading...' : 'No current stock data available'
                  }}
            />
              </div>
              </Spin>
            <div className="edit-reference-modal-footer">
              <div className="edit-reference-modal-pagination-controls">
                <Pagination
                  current={currentStockPagination.current}
                  pageSize={currentStockPagination.pageSize}
                  total={currentStock.length}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`}
                  pageSizeOptions={['10', '20', '50', '100']}
                  onChange={(page, pageSize) => {
                    setCurrentStockPagination({ current: page, pageSize: pageSize || 10 });
                    setSelectedRowIds([]); // Clear selection when page changes
                  }}
                  onShowSizeChange={(current, size) => {
                    setCurrentStockPagination({ current: 1, pageSize: size });
                    setSelectedRowIds([]); // Clear selection when page size changes
                  }}
                />
              </div>
              <div className="edit-reference-modal-buttons">
                <Button 
                  danger 
                  disabled={selectedRowIds.length === 0 || isTagActionLoading || loadingModalData}
                  loading={isTagActionLoading || loadingModalData}
                  onClick={() => setDeleteConfirmVisible(true)}
                >
                  Delete tag from Reference Stock
                </Button>
              </div>
            </div>
            </>
          ) : (
            <>
              <Spin spinning={loadingModalData || isTagActionLoading} tip={isTagActionLoading ? "Processing tag operation..." : "Loading scanned tags..."}>
              <div className="edit-reference-stock-table-wrapper">
              <Table
                  rowSelection={handleRowSelection}
                  className="edit-reference-stock-table"
                columns={[
                    { 
                      title: 'Product Description', 
                      dataIndex: 'description', 
                      key: 'description', 
                      align: 'left',
                      ellipsis: {
                        showTitle: false,
                      },
                      render: (text: string, record: any) => {
                        const fullDescription = text || record?.productDescription || '-';
                        return (
                          <UITooltip 
                            title={fullDescription} 
                            placement="top"
                            overlayStyle={{ maxWidth: '800px', wordWrap: 'break-word', whiteSpace: 'normal' }}
                            getPopupContainer={(trigger: HTMLElement) => trigger.parentElement || document.body}
                          >
                            <div style={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              maxWidth: '100%'
                            }}>
                              {fullDescription}
                            </div>
                          </UITooltip>
                        );
                      }
                    },
                    { title: 'EPC', dataIndex: 'epc', key: 'epc', align: 'left' },
                    { title: 'TagID', dataIndex: 'tagId', key: 'tagId', align: 'left', render: (text: string) => text || '-' },
                    { title: 'RSSI', dataIndex: 'rssi', key: 'rssi', align: 'left', render: (text: any) => text || '-' },
                    { title: 'Port', dataIndex: 'port', key: 'port', align: 'left', render: (text: any) => text || '-' },
                    { title: 'UPC', dataIndex: 'upc', key: 'upc', align: 'left', render: (text: string) => text || '-' }
                  ]}
                  dataSource={scannedTags.slice(
                    (scannedTagsPagination.current - 1) * scannedTagsPagination.pageSize,
                    scannedTagsPagination.current * scannedTagsPagination.pageSize
                  )}
                  rowKey={(record: any, index?: number) => {
                    const uniqueId = record?.id || record?.key || `scanned-${record?.epc || ''}-${index ?? 0}`;
                    return uniqueId || `scanned-row-${index ?? 0}`;
                  }}
                pagination={false}
              scroll={{ x: 'max-content', y: 500 }}
                  locale={{
                    emptyText: (loadingModalData || isTagActionLoading) ? 'Loading...' : 'No scanned tags available'
                  }}
            />
              </div>
              </Spin>
            <div className="edit-reference-modal-footer">
              <div className="edit-reference-modal-pagination-controls">
                <Pagination
                  current={scannedTagsPagination.current}
                  pageSize={scannedTagsPagination.pageSize}
                  total={scannedTags.length}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`}
                  pageSizeOptions={['10', '20', '50', '100']}
                  onChange={(page, pageSize) => {
                    setScannedTagsPagination({ current: page, pageSize: pageSize || 10 });
                    setSelectedRowIds([]); // Clear selection when page changes
                  }}
                  onShowSizeChange={(current, size) => {
                    setScannedTagsPagination({ current: 1, pageSize: size });
                    setSelectedRowIds([]); // Clear selection when page size changes
                  }}
                />
              </div>
              <div className="edit-reference-modal-buttons">
                <Button 
                  type="primary"
                  style={{ backgroundColor: '#227f99', borderColor: '#227f99' }}
                  disabled={selectedRowIds.length === 0 || isTagActionLoading || loadingModalData}
                  loading={isTagActionLoading || loadingModalData}
                  onClick={() => setAddConfirmVisible(true)}
                >
                  Add tag to Reference Stock
                </Button>
              </div>
            </div>
            </>
          )}
        
        {/* Add Tags Confirmation Modal */}
        <UIConfirmModal
          visible={addConfirmVisible}
          type={ModalType.WARN}
          primaryText="Add tag to reference stock?"
          confirmButton={ModalButton.CONFIRM}
          cancelButton={ModalButton.CANCEL}
          iconNode="➕"
          confirmCallback={handleAddConfirm}
          cancelSiteButton={() => setAddConfirmVisible(false)}
        />
        
        {/* Delete Tags Confirmation Modal */}
        <UIConfirmModal
          visible={deleteConfirmVisible}
          type={ModalType.WARN}
          primaryText="Are you sure you want to delete this tag from reference stock?"
          confirmButton={ModalButton.CONFIRM}
          cancelButton={ModalButton.CANCEL}
          confirmCallback={handleDeleteConfirm}
          cancelSiteButton={() => setDeleteConfirmVisible(false)}
        />
        </Modal>
    </>
  );
};
